import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  Alert,
  Image,
  TextInput,
  Modal,
  Dimensions,
  useWindowDimensions,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from './DirectionalIcon';
import * as ImagePicker from 'expo-image-picker';
import * as DocumentPicker from 'expo-document-picker';
import * as Clipboard from 'expo-clipboard';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { Input } from './design-system/Input';
import { PhoneInputComponent } from './design-system/PhoneInput';
import { Button } from './design-system/Button';
import { CountryPicker } from './design-system/CountryPicker';
import { MapLocationPicker } from './MapLocationPicker';
import { ConfirmationModal } from './Modal';
import { NotesSection, Note } from './NotesSection';
import { ContactFlowRuns } from './ContactFlowRuns';
import { ContactQuickActions } from './ContactQuickActions';
import { ContactVisitsCard } from './ContactVisitsCard';
import { ContactVisitWorkspace } from './visits/ContactVisitWorkspace';
import { PaneHeader, PaneHeaderIconButton } from './PaneHeader';
import { UserAssignmentModal } from './UserAssignmentModal';
import { DataListFieldRenderer } from './data-lists/DataListFieldRenderer';
import { apiService, DataList, DataListColumn } from '../services/api';
import { getCurrencySymbol } from '../utils/currencies';
import { useAppStore } from '../store/appStore';
import { useAppContext } from '../contexts/AppContext';
import { getImageUrlSync, getApiBaseURL } from '../utils/imageProxy';
import { parseInternationalPhoneNumber, getNationalNumberFromPhone, getCountryFromPhone } from '../utils/phoneNumber';
import { getCountryCallingCode, parsePhoneNumber, CountryCode } from 'libphonenumber-js';
import { useLocale } from '../contexts/LocaleContext';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { usePermissions } from '../hooks/usePermissions';
import { useConversationHandler } from '../hooks/useConversationHandler';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { usePhoneFormGutter } from '../hooks/usePhoneFormGutter';
import { useBusinessData } from '../hooks/useBusinessData';
import { withAlpha } from '../theme';
import { businessCurrency } from '../utils/businessDefaults';
import { defaultPhoneCountry } from '../utils/businessDefaults';

interface Contact {
  id: string;
  name: string;
  channel: 'whatsapp' | 'facebook-messenger' | 'x-twitter' | 'phone' | 'telegram' | 'email';
  phoneNumber?: string;
  email?: string;
  profilePictureUrl?: string;
  isOnline?: boolean;
  isBusiness?: boolean;
  metadata?: any;
  parentContact?: any;
}

interface ContactEditFormProps {
  contact: Contact | null;
  /** Open this visit's workspace on load (the visit page renders the
   * contact profile with its visit already open). */
  initialActiveVisitId?: string;
  /** The open visit's workspace was closed — `ended` when the visit was just ended. */
  onVisitClosed?: (ended?: boolean) => void;
  onClose?: () => void;
  onSave?: (contact: Contact) => void;
  onDelete?: (contactId: string) => void;
  compact?: boolean;
  /** Set when this contact was reached via a system Contacts Data List's
   * scoped mode (see ContactsListScreen.tsx) — enables the Extra Fields
   * section (item 2) for that list's customColumns, if any. Threaded here
   * (rather than through RightPane/ScreenLayout) since this form already
   * owns fetching/saving everything else about the contact. */
  listId?: string;
  /** When provided, the channels row's tap handler calls this instead of
   * its own default (handleChannelPress → useConversationHandler's
   * startConversation, which navigates to a full conversation screen) —
   * used by the master-detail pane flow (Home/Contacts/Chat) to open the
   * tapped channel's conversation in the adjacent right pane instead.
   * Omitted everywhere else, which keeps today's default behavior exactly
   * as it was. */
  onChannelSelect?: (channel: string, identifierValue?: string, channelContactId?: string) => void;
  /** When provided, tapping a phone identifier calls this instead of the
   * default navigateTo('call-history', ...) full-screen navigation — used by
   * the master-detail pane flow to show Call History in the adjacent right
   * pane instead of leaving the persistent shell entirely (navigateTo to a
   * screen outside the ['home','contacts','chat'] shell group tears down and
   * rebuilds the whole chrome, the exact "loads from scratch" behavior the
   * pane flow exists to avoid). Omitted everywhere else, which keeps today's
   * default behavior exactly as it was. */
  onViewCallHistory?: (contactId: string, contactName: string, phoneNumber?: string, phoneIndex?: number) => void;
  /** Triggers non-editing profile cards (assignments, lists, orders, etc.) to
   * refresh without rehydrating the editable form fields and wiping drafts. */
  profileRefreshKey?: string | number;
  /** Called after a profile card mutates related contact data. Parent panes
   * can refresh lists/filters without closing this edit/profile view. */
  onProfileDataChange?: () => void;
}

interface ChannelIdentifier {
  key: string; // stable per-row id — array index alone breaks React reconciliation across add/remove
  type: 'phone' | 'email' | 'whatsapp' | 'facebook-messenger' | 'instagram' | 'telegram';
  value: string;
  channelContactId?: string;
  countryCode?: string; // ISO country code (e.g., 'EG', 'SA', 'US') for phone/whatsapp
  isPrimary?: boolean;
  // True only for a row added fresh by the "+" menu THIS session — gates the
  // "Add for WhatsApp/Phone" quick action to first-add time only, never shown
  // for an identifier that was saved/loaded from the server.
  isFreshlyAdded?: boolean;
  // Set on the SOURCE row after its quick-add button is used — holds the
  // `key` of the sibling row it created OR overwrote, so the button can flip
  // to "Undo" and reverse exactly that action. Cleared (back to showing
  // "Add for X") if the user undoes it.
  quickAddedKey?: string;
  // Set on a row whose value was OVERWRITTEN by addSiblingPhoneIdentifier
  // (as opposed to a brand-new row being inserted) — holds the value it had
  // right before the overwrite, so undo can restore it instead of deleting
  // the row outright (it existed before the quick-add action; only the
  // freshly-inserted-row case should actually remove a row on undo).
  preQuickAddValue?: string;
}

interface ContactAttachment {
  _id?: string;
  name: string;
  type: string;
  size: number;
  url: string;
  storagePath?: string;
  uploadedBy?: string;
  uploadedAt?: Date;
}

// Mirrors OmniContact's assignment/assignments shape (findById's accessCheck-
// resolved form, sendifier_api/.../omni-contact.entity.ts) — only the fields
// the Assignment card actually renders.
interface ContactAssignmentEntry {
  _id?: string;
  assignedTo?: string | { _id?: string; firstName?: string; lastName?: string; email?: string; name?: string } | null;
  departmentId?: string | { _id?: string; name?: string; description?: string } | null;
  status?: string;
  assignedAt?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ContactOrderSummary {
  _id: string;
  orderNumber?: string;
  orderStatus?: string;
  paymentStatus?: string;
  totalPrice?: number;
  currency?: string;
  items?: any[];
  orderDate?: string;
}

interface ContactQuotationSummary {
  _id: string;
  quotationNumber?: string;
  quotationStatus?: string;
  totalPrice?: number;
  currency?: string;
  items?: any[];
  validUntil?: string;
}

type ContactCardActionSheet =
  | { kind: 'order-status'; id: string; title: string; current?: string }
  | { kind: 'payment-status'; id: string; title: string; current?: string }
  | { kind: 'quotation-status'; id: string; title: string; current?: string };

let identifierKeySeq = 0;
const genIdentifierKey = () => `id_${Date.now()}_${identifierKeySeq++}`;

// Phone-number-based channels that can meaningfully share the same number —
// used to offer "Add for X" quick actions right after adding one of these for
// the first time. Telegram is intentionally excluded: its field in this form
// is free-text (a username), not a phone number, per product decision.
const PHONE_BASED_CHANNELS: Array<ChannelIdentifier['type']> = ['phone', 'whatsapp'];

const { width: screenWidth, height: screenHeight } = Dimensions.get('window');
const isWebPlatform = Platform.OS === 'web';

/** Converts a touch event's DOCUMENT-relative pageX/pageY into WINDOW/
 * viewport-relative coordinates — mirrors ContactsList.tsx's identical
 * helper. Needed because Modal portals to document.body with
 * position:'fixed' (viewport-anchored) on web, while pageX/pageY include
 * page scroll per the DOM spec; without this conversion the popover drifts
 * further from the tapped icon the more the page has scrolled. No-op on
 * native, where pageX/pageY are already what RN's Modal expects. */
const toWindowRelative = (pageX: number, pageY: number): { x: number; y: number } => {
  if (!isWebPlatform || typeof window === 'undefined') return { x: pageX, y: pageY };
  return { x: pageX - window.scrollX, y: pageY - window.scrollY };
};

/** Formats a phone/whatsapp identifier row for DISPLAY with its country
 * calling code prefixed — identifier.value itself is always the bare
 * national number (country code stripped at load time, see
 * getNationalNumberFromPhone's call sites above), which is correct for
 * PhoneInputComponent's own country-picker + national-number split editing
 * UX, but reads as a suspiciously incomplete number anywhere else (e.g. the
 * channel icon picker) without its code re-attached for display only. */
const formatIdentifierForDisplay = (identifier: ChannelIdentifier): string => {
  if (identifier.type !== 'phone' && identifier.type !== 'whatsapp') return identifier.value;
  if (!identifier.countryCode) return identifier.value;
  try {
    const callingCode = getCountryCallingCode(identifier.countryCode as CountryCode);
    return `+${callingCode}${identifier.value}`;
  } catch {
    return identifier.value;
  }
};

const formatIdentifierForOpen = (identifier: ChannelIdentifier): string => {
  const value = formatIdentifierForDisplay(identifier).trim();
  if (identifier.type !== 'phone' && identifier.type !== 'whatsapp') return value;
  return value.startsWith('+') ? value : value.replace(/\s+/g, '');
};

const normalizeIdentifierForDedupe = (identifier: Pick<ChannelIdentifier, 'type' | 'value' | 'countryCode'>): string => {
  const displayValue = formatIdentifierForDisplay(identifier as ChannelIdentifier);
  const normalized = displayValue
    .replace(/^\+/, '')
    .replace(/[\s().-]/g, '')
    .trim()
    .toLowerCase();
  return `${identifier.type}:${normalized}`;
};

const dedupeChannelIdentifiers = (identifiers: ChannelIdentifier[]): ChannelIdentifier[] => {
  const seen = new Set<string>();
  return identifiers.filter((identifier) => {
    if (!identifier.value.trim()) return true;
    const key = normalizeIdentifierForDedupe(identifier);
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const normalizeAssignmentRecord = (assignment: any): ContactAssignmentEntry => ({
  _id: assignment?._id || assignment?.id,
  assignedTo: assignment?.assignedTo || assignment?.userId || assignment?.user || undefined,
  departmentId: assignment?.departmentId || assignment?.assignedDepartment || assignment?.department || undefined,
  status: assignment?.status,
  assignedAt: assignment?.assignedAt,
  createdAt: assignment?.createdAt,
  updatedAt: assignment?.updatedAt,
});

const assignmentArrayFrom = (value: any): any[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray(value.assignments)) return value.assignments;
  if (Array.isArray(value.items)) return value.items;
  if (Array.isArray(value.data)) return value.data;
  return [value];
};

const addAssignmentTarget = (
  targets: Map<string, { targetType: string; targetId: string }>,
  targetType?: string,
  targetId?: string
) => {
  if (!targetType || !targetId) return;
  targets.set(`${targetType}:${targetId}`, { targetType, targetId });
};

const getAssignmentTargetTypeForChannel = (channel?: string) => {
  switch (channel) {
    case 'whatsapp':
      return 'whatsapp_contact';
    case 'facebook-messenger':
    case 'facebook':
      return 'facebook_contact';
    case 'instagram':
      return 'instagram_contact';
    case 'telegram':
      return 'telegram_contact';
    case 'email':
      return 'email_contact';
    case 'phone':
      return 'phone_contact';
    default:
      return undefined;
  }
};

const getAssignmentTargets = (contactId: string, parentContact?: any) => {
  const targets = new Map<string, { targetType: string; targetId: string }>();
  addAssignmentTarget(targets, 'omni_contact', contactId);
  addAssignmentTarget(targets, 'omni_contact', parentContact?._id || parentContact?.id);

  (parentContact?.linkedChannelContacts || []).forEach((linkedContact: any) => {
    const targetId = linkedContact?.channelContactId
      || linkedContact?.contactId
      || linkedContact?.sourceContactId
      || linkedContact?._id
      || linkedContact?.id;
    const targetType = linkedContact?.targetType || getAssignmentTargetTypeForChannel(linkedContact?.channel || linkedContact?.type);
    addAssignmentTarget(targets, targetType, targetId);
  });

  // Older assignment rows were created directly against the visible contact id
  // before all contact panes consistently used the omni parent id.
  addAssignmentTarget(targets, 'whatsapp_contact', contactId);
  addAssignmentTarget(targets, 'facebook_contact', contactId);

  return Array.from(targets.values());
};

const getAssignmentTimestamp = (assignment: ContactAssignmentEntry) =>
  new Date(assignment.assignedAt || assignment.createdAt || assignment.updatedAt || 0).getTime();

const getAssignmentSortPriority = (assignment: ContactAssignmentEntry) => {
  const status = assignment.status || 'active';
  return status === 'active' || status === 'inProgress' ? 0 : 1;
};

const sortAssignmentsForProfile = (assignments: ContactAssignmentEntry[]) =>
  [...assignments].sort((a, b) => {
    const statusPriority = getAssignmentSortPriority(a) - getAssignmentSortPriority(b);
    if (statusPriority !== 0) return statusPriority;
    return getAssignmentTimestamp(b) - getAssignmentTimestamp(a);
  });

const mergeAssignmentRecords = (...groups: any[][]): ContactAssignmentEntry[] => {
  const byKey = new Map<string, ContactAssignmentEntry>();
  groups.flat().filter(Boolean).forEach((assignment, index) => {
    const normalized = normalizeAssignmentRecord(assignment);
    const assignmentDate = normalized.assignedAt || normalized.createdAt || normalized.updatedAt || '';
    const key = normalized._id
      || `${typeof normalized.assignedTo === 'string' ? normalized.assignedTo : normalized.assignedTo?._id || ''}:${typeof normalized.departmentId === 'string' ? normalized.departmentId : normalized.departmentId?._id || ''}:${normalized.status || ''}:${assignmentDate || index}`;
    const existing = byKey.get(key);
    byKey.set(key, {
      ...normalized,
      ...existing,
      assignedTo: existing?.assignedTo || normalized.assignedTo,
      departmentId: existing?.departmentId || normalized.departmentId,
      assignedAt: existing?.assignedAt || normalized.assignedAt,
      createdAt: existing?.createdAt || normalized.createdAt,
      updatedAt: existing?.updatedAt || normalized.updatedAt,
    });
  });
  return sortAssignmentsForProfile(Array.from(byKey.values()));
};

export const ContactEditForm: React.FC<ContactEditFormProps> = ({
  initialActiveVisitId,
  onVisitClosed,
  contact,
  onClose,
  onSave,
  onDelete,
  compact = false,
  listId,
  onChannelSelect,
  onViewCallHistory,
  profileRefreshKey,
  onProfileDataChange,
}) => {
  const styles = useThemedStyles(getStyles);
  const phoneFormGutter = usePhoneFormGutter();
  // Phone-width screens get the full-screen compact layout (top bar with
  // back + Save/Delete, quick actions on their own row) instead of the
  // desktop pane's single avatar+actions row.
  const { width: windowWidth } = useWindowDimensions();
  const isPhoneWidth = windowWidth < designTokens.breakpoints.tablet;
  // The pane (compact) layout puts sections side by side; on a phone that
  // squeezes them, so they stack instead and the summary cards become a
  // swipeable strip.
  const isPhonePane = compact && isPhoneWidth;
  const phoneCardWidth = isPhonePane ? Math.min(340, Math.round(windowWidth * 0.82)) : undefined;
  const ProfileCardsStrip: any = isPhonePane ? ScrollView : React.Fragment;
  const { currentBusiness, user, setRightPaneCollapsed, contactsRefreshToken, bumpContactsRefreshToken } = useAppStore();
  const { showNotification, navigateTo } = useAppContext();
  const { users: businessUsers, departments: businessDepartments } = useBusinessData({ autoRefresh: false });
  const { startConversation } = useConversationHandler();
  const { t } = useLocale();
  const rtl = useRTLStyles();
  const { hasPermissionAtLeast } = usePermissions();
  const canManageContacts = hasPermissionAtLeast('contacts.manage', 'edit');
  // Deleting is stricter than editing fields — requires the 'admin' tier of
  // contacts.manage, matching the backend's own canDeleteContacts gate.
  const canDeleteContacts = hasPermissionAtLeast('contacts.manage', 'admin');
  const canManageTags = hasPermissionAtLeast('contacts.manageTags', 'edit');
  const canManageNotes = hasPermissionAtLeast('contacts.manageNotes', 'edit');
  const [name, setName] = useState('');
  const [avatar, setAvatar] = useState<string | null>(null);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const [channelIdentifiers, setChannelIdentifiers] = useState<ChannelIdentifier[]>([]);
  // Identifiers the user explicitly removed via the row's × button this session —
  // sent to the backend as removedIdentifiers so it can tell "explicitly deleted"
  // apart from "masked/omitted, leave alone". Only real (non-masked-placeholder)
  // values are recorded — removing a still-masked row means the user never saw
  // the real number to begin with, so there's nothing meaningful to instruct the
  // backend to delete; it stays untouched server-side either way.
  const removedIdentifiersRef = useRef<Array<{ type: string; value: string; index?: number }>>([]);
  const lastContactsRefreshTokenRef = useRef(contactsRefreshToken);
  const [location, setLocation] = useState<{
    latitude?: number;
    longitude?: number;
    address?: string;
    city?: string;
    country?: string;
  } | null>(null);
  // Deliberately separate from location — nationality isn't a place the
  // contact currently is (it doesn't come from the map picker), it's a
  // standalone demographic detail grouped under the same Address section
  // in the UI for convenience.
  const [nationality, setNationality] = useState('');
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  // Collapsed by default — the paste-a-link shortcut is a power-user escape
  // hatch, not something most edits need visible alongside the map button.
  const [showMapsUrlInput, setShowMapsUrlInput] = useState(false);
  // Measured heights (compact mode only) so the avatar circle and the map
  // thumb can match the height of the content stacked beside them (name+
  // channels, and the address fields, respectively) — RN flexbox has no
  // "equal height columns" primitive, so this reads the sibling's actual
  // rendered height via onLayout and applies it directly.
  const [identityColumnHeight, setIdentityColumnHeight] = useState<number | null>(null);
  const [locationFieldsHeight, setLocationFieldsHeight] = useState<number | null>(null);
  // Paste-a-link alternative to dropping a pin — resolves a Google/Apple
  // Maps share URL server-side (handles short links + both providers' URL
  // shapes) into the same address/city/country shape the map picker itself
  // produces, via the same underlying geocoding provider.
  const [mapsUrlInput, setMapsUrlInput] = useState('');
  const [resolvingMapsUrl, setResolvingMapsUrl] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [newTag, setNewTag] = useState('');
  const [tagSearch, setTagSearch] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [attachments, setAttachments] = useState<ContactAttachment[]>([]);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [removingAttachmentUrl, setRemovingAttachmentUrl] = useState<string | null>(null);
  // Same underlying data/endpoints as ConversationScreen's Notes panel and
  // PhoneCallScreen's recent-notes preview (getParentContactById's nested
  // notes / addContactNote / deleteContactNote) — this being a full edit
  // screen (not a compact in-call/pane panel), all notes are shown rather
  // than capping to a preview count.
  const [notes, setNotes] = useState<Note[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);
  const [contactAssignments, setContactAssignments] = useState<ContactAssignmentEntry[]>([]);
  const [showAssignmentModal, setShowAssignmentModal] = useState(false);
  const [orders, setOrders] = useState<ContactOrderSummary[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [quotations, setQuotations] = useState<ContactQuotationSummary[]>([]);
  const [loadingQuotations, setLoadingQuotations] = useState(false);
  const [contactDataLists, setContactDataLists] = useState<DataList[]>([]);
  const [availableContactDataLists, setAvailableContactDataLists] = useState<DataList[]>([]);
  const [loadingContactDataLists, setLoadingContactDataLists] = useState(false);
  const [updatingDataListId, setUpdatingDataListId] = useState<string | null>(null);
  const [cardActionSheet, setCardActionSheet] = useState<ContactCardActionSheet | null>(null);
  const [updatingCardAction, setUpdatingCardAction] = useState<string | null>(null);
  const [showIdentifierMenu, setShowIdentifierMenu] = useState(false);
  // Toggles the compact "manage channels" editor below the tap-to-open icon
  // row — the icon row itself is launch-only (tap = open conversation), so
  // editing raw values (add/remove a channel, change a number/handle) lives
  // behind this separate pencil-triggered panel instead.
  const [showChannelsEditor, setShowChannelsEditor] = useState(false);
  // Icon-row picker — when a channel type has more than one identifier (e.g.
  // two WhatsApp numbers), the icon row below shows ONE icon per type (not
  // one per raw entry) with a badge; tapping it opens this picker to choose
  // which entry to act on, mirroring ContactsList.tsx's contact-card
  // call/chat icon pickers.
  const [channelIconPickerType, setChannelIconPickerType] = useState<ChannelIdentifier['type'] | null>(null);
  const [channelIconPickerPosition, setChannelIconPickerPosition] = useState<{ x: number; y: number } | null>(null);
  const [apiBaseURL, setApiBaseURL] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right?: number; left?: number } | null>(null);
  const phoneInputRefs = React.useRef<Record<string, any>>({});
  const identifierMenuRef = React.useRef<View>(null);
  const addButtonRef = React.useRef<View>(null);

  // Extra fields (item 2) — custom columns on the linked system Contacts
  // Data List, alongside this contact's own fixed fields above.
  const [customColumns, setCustomColumns] = useState<DataListColumn[]>([]);
  const [customValues, setCustomValues] = useState<Record<string, any>>({});
  const [customValueErrors, setCustomValueErrors] = useState<Record<string, string>>({});
  const [savingCustomValues, setSavingCustomValues] = useState(false);

  const notifyProfileCardMutation = useCallback(() => {
    bumpContactsRefreshToken();
    onProfileDataChange?.();
  }, [bumpContactsRefreshToken, onProfileDataChange]);

  // The visit being worked inside this profile (see ContactVisitWorkspace) —
  // opened from the Visits card, or automatically for the agent's own
  // visit in progress with this contact.
  const [activeVisitId, setActiveVisitId] = useState<string | null>(null);
  const [visitsRefresh, setVisitsRefresh] = useState(0);
  useEffect(() => { setActiveVisitId(initialActiveVisitId || null); }, [contact?.id, initialActiveVisitId]);

  // Vendor created from this contact ("Add as vendor") — shown as a link.
  const [linkedVendor, setLinkedVendor] = useState<{ id: string; name: string } | null>(null);
  useEffect(() => {
    setLinkedVendor(null);
    const parentId = (contact as any)?.parentContact?._id || contact?.id;
    if (!parentId || !contact) return;
    apiService.getVendors({ contactId: String(parentId), limit: 1 })
      .then((res) => {
        const v = res.statusCode === 1000 ? res.data?.vendors?.[0] : undefined;
        if (v) setLinkedVendor({ id: String(v._id), name: v.name });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id, profileRefreshKey, visitsRefresh]);

  useEffect(() => {
    if (!listId || !contact?.id) return;
    apiService.getDataList(listId).then((res) => {
      if (res.statusCode === 1000 && res.data?.customColumns?.length) {
        setCustomColumns(res.data.customColumns);
      }
    }).catch((err) => console.error('[ContactEditForm] Failed to load linked list:', err));
    apiService.getDataListRecord(listId, contact.id).then((res) => {
      if (res.statusCode === 1000 && res.data) {
        setCustomValues(res.data.values || {});
      }
    }).catch((err) => console.error('[ContactEditForm] Failed to load custom field values:', err));
  }, [listId, contact?.id]);

  const handleSaveCustomValues = async () => {
    if (!listId || !contact?.id) return;
    try {
      setSavingCustomValues(true);
      const res = await apiService.upsertDataListRecordCustomValues(listId, contact.id, customValues);
      if (res.statusCode === 1000) {
        showNotification('success', t('contactEdit.success.title'), t('contactEdit.cards.extraFieldsUpdated'));
        setCustomValueErrors({});
        notifyProfileCardMutation();
      } else {
        showNotification('error', t('contactEdit.errors.title'), res.errors?.[0]?.message || t('contactEdit.cards.extraFieldsFailed'));
      }
    } catch (error: any) {
      showNotification('error', t('contactEdit.errors.title'), error.message || t('contactEdit.cards.extraFieldsFailed'));
    } finally {
      setSavingCustomValues(false);
    }
  };

  // Load API base URL for image proxy
  useEffect(() => {
    const loadApiBaseURL = async () => {
      const baseURL = await getApiBaseURL();
      setApiBaseURL(baseURL);
    };
    loadApiBaseURL();
  }, []);

  // Load contact data when contact changes
  useEffect(() => {
    removedIdentifiersRef.current = [];
    if (contact) {
      console.log('📝 [ContactEditForm] Loading contact:', contact);
      const parentContact = (contact as any).parentContact;
      
      if (parentContact) {
        setName(parentContact.profile?.name || contact.name || '');
        setAvatar(parentContact.profile?.avatar || contact.profilePictureUrl || null);
        setLocation(parentContact.profile?.location || null);
        setNationality(parentContact.profile?.location?.nationality || '');
        setTags(parentContact.profile?.tags || []);
        setAttachments(parentContact.attachments || []);

        // isPrimary lookup — linkedChannelContacts carries the authoritative flag
        // per specific identifier value; matched by normalized (no leading '+')
        // value since identifiers[] and linkedChannelContacts can store either form.
        const normalizeForMatch = (val: string) => (val || '').replace(/^\+/, '').replace(/[\s().-]/g, '').trim();
        const linkedChannelContacts: any[] = parentContact.linkedChannelContacts || [];
        const findLinkedChannelContact = (type: string, value: string) => {
          return linkedChannelContacts.find(
            (lc: any) => lc.channel === type && normalizeForMatch(lc.channelIdentifier) === normalizeForMatch(value)
          );
        };
        const findIsPrimary = (type: string, value: string): boolean | undefined => findLinkedChannelContact(type, value)?.isPrimary;
        const findChannelContactId = (type: string, value: string): string | undefined => findLinkedChannelContact(type, value)?.channelContactId;

        // For phone/whatsapp, splits a stored value (which may or may not
        // already carry its country code, e.g. "201234567890" vs
        // "1234567890") into the bare national number + detected ISO
        // country code — the shape every phone/whatsapp ChannelIdentifier
        // needs so handleSave can later recombine them correctly. Shared by
        // BOTH identifier-shape branches below.
        const processPhoneIdentifier = (type: string, value: string): { value: string; countryCode?: string } => {
          if (type !== 'phone' && type !== 'whatsapp') return { value };
          let phoneToDetect = value;
          if (!phoneToDetect.startsWith('+')) {
            phoneToDetect = `+${phoneToDetect}`;
          }
          const detectedCountry = getCountryFromPhone(phoneToDetect);
          const countryCode = detectedCountry || defaultPhoneCountry(); // Default to Egypt if not detected
          const nationalNumber = getNationalNumberFromPhone(phoneToDetect);
          return { value: nationalNumber, countryCode };
        };

        // Load channel identifiers from parent contact
        const identifiers: ChannelIdentifier[] = [];
        if (parentContact.identifiers) {
          if (Array.isArray(parentContact.identifiers)) {
            // Confirmed real bug: this branch never called
            // processPhoneIdentifier at all — every phone/whatsapp
            // identifier loaded via THIS shape kept its raw stored value
            // (country code still attached) and NO countryCode field,
            // silently violating formatIdentifierForDisplay's own
            // documented invariant that id.value is always the bare
            // national number. handleSave's countryCode-based recombination
            // (added to fix duplication-on-save) had nothing to recombine
            // for these rows, so it fell through to the still-fragile
            // ref-based fallback and reproduced the exact same duplication
            // bug for any contact whose `identifiers` field the backend
            // returns in this Array shape (confirmed as a real, currently-
            // occurring response shape — ContactsList.tsx handles the same
            // Array-vs-Object ambiguity for this identical field).
            parentContact.identifiers.forEach((id: any) => {
              if (id.type && id.value) {
                const { value: processedValue, countryCode } = processPhoneIdentifier(id.type, id.value);
                identifiers.push({ key: genIdentifierKey(), type: id.type, value: processedValue, countryCode, isPrimary: findIsPrimary(id.type, id.value), channelContactId: findChannelContactId(id.type, id.value) });
              }
            });
          } else if (typeof parentContact.identifiers === 'object') {
            Object.keys(parentContact.identifiers).forEach((key) => {
              const values = parentContact.identifiers[key];
              if (Array.isArray(values)) {
                values.forEach((value: string) => {
                  // Map key to identifier type
                  const type = key === 'whatsapp' ? 'whatsapp' as const :
                              key === 'facebook-messenger' ? 'facebook-messenger' as const :
                              key === 'instagram' ? 'instagram' as const :
                              key === 'telegram' ? 'telegram' as const :
                              key === 'phone' ? 'phone' as const :
                              key === 'email' ? 'email' as const : null;
                  if (type) {
                    const { value: processedValue, countryCode } = processPhoneIdentifier(type, value);
                    identifiers.push({ key: genIdentifierKey(), type, value: processedValue, countryCode, isPrimary: findIsPrimary(type, value), channelContactId: findChannelContactId(type, value) });
                  }
                });
              }
            });
          }
        }
        // If no identifiers found, use profile data
        if (identifiers.length === 0) {
          if (parentContact.profile?.phone) {
            // Extract national number and detect country code
            let phoneToDetect = parentContact.profile.phone;
            // If it doesn't start with +, try adding it for detection
            if (!phoneToDetect.startsWith('+')) {
              phoneToDetect = `+${phoneToDetect}`;
            }
            const detectedCountry = getCountryFromPhone(phoneToDetect);
            const countryCode = detectedCountry || defaultPhoneCountry();
            const nationalNumber = getNationalNumberFromPhone(phoneToDetect);
            identifiers.push({ key: genIdentifierKey(), type: 'phone', value: nationalNumber, countryCode });
          }
          if (parentContact.profile?.email) {
            identifiers.push({ key: genIdentifierKey(), type: 'email', value: parentContact.profile.email });
          }
        }
        setChannelIdentifiers(dedupeChannelIdentifiers(identifiers));
      } else {
        setName(contact.name || '');
        setAvatar(contact.profilePictureUrl || null);
        setChannelIdentifiers([
          ...(contact.phoneNumber ? (() => {
            let phoneToDetect = contact.phoneNumber;
            // If it doesn't start with +, try adding it for detection
            if (!phoneToDetect.startsWith('+')) {
              phoneToDetect = `+${phoneToDetect}`;
            }
            const detectedCountry = getCountryFromPhone(phoneToDetect);
            const countryCode = detectedCountry || defaultPhoneCountry();
            const nationalNumber = getNationalNumberFromPhone(phoneToDetect);
            return [{ key: genIdentifierKey(), type: 'phone' as const, value: nationalNumber, countryCode }];
          })() : []),
          ...(contact.email ? [{ key: genIdentifierKey(), type: 'email' as const, value: contact.email }] : []),
        ]);
        setAttachments([]);
      }
    } else {
      // Reset form for new contact
      setName('');
      setAvatar(null);
      setChannelIdentifiers([]);
      setLocation(null);
      setNationality('');
      setTags([]);
      setAttachments([]);
    }
    // Keyed on contact?.id, NOT the whole `contact` object — confirmed real bug:
    // ContactsListScreen's live contact-list refresh (socket updates, periodic
    // refetch) calls setSelectedContact(updated) with a brand-new object for the
    // SAME contact whenever the underlying list changes, even while this form is
    // open mid-edit. Depending on the whole object meant every such refresh
    // silently reset the entire form back to server state — wiping unsaved
    // typed values AND the isFreshlyAdded/quickAddedKey flags the "Add for
    // Phone/WhatsApp" quick-action relies on, which is why it intermittently
    // never appeared. Only a genuinely different contact (a different id)
    // should re-sync the form from scratch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contact?.id]);

  // External contact mutations (for example "set as contact location" from
  // the conversation pane) refresh the contacts list via the global token, but
  // this form intentionally does not rehydrate all editable fields for the
  // same contact id. Pull only server-owned side data so the open profile
  // reflects location/tags/documents without wiping draft identity edits.
  useEffect(() => {
    if (lastContactsRefreshTokenRef.current === contactsRefreshToken) return;
    lastContactsRefreshTokenRef.current = contactsRefreshToken;
    if (!contact?.id) return;

    let cancelled = false;
    apiService.getParentContactById(contact.id)
      .then((result) => {
        if (cancelled || result.statusCode !== 1000 || !result.data) return;
        const parentContact = result.data;
        setLocation(parentContact.profile?.location || null);
        setNationality(parentContact.profile?.location?.nationality || '');
        setTags(parentContact.profile?.tags || []);
        setAttachments(parentContact.attachments || []);
      })
      .catch((error) => {
        console.error('[ContactEditForm] Failed to refresh contact side data:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [contact?.id, contactsRefreshToken]);

  // Notes + assignment — contact.id is the omni-contact (parent) id, same id
  // handleSave already uses for apiService.updateParentContact. Only loadable
  // for an existing contact; a new (unsaved) contact has no id to attach
  // notes to. Assignments may exist on the omni contact or older channel
  // contact targets, so merge every compatible source before showing the
  // recent preview.
  const loadNotes = useCallback(async (id: string) => {
    setLoadingNotes(true);
    setContactAssignments([]);
    try {
      const result = await apiService.getParentContactById(id);
      if (result.statusCode === 1000 && result.data) {
        setNotes(result.data.notes || []);
        const fallbackAssignments = [
          ...assignmentArrayFrom(result.data.assignments),
          ...assignmentArrayFrom(result.data.assignment),
        ];
        let nextAssignments = mergeAssignmentRecords(fallbackAssignments);
        setContactAssignments(nextAssignments);

        const cacheBuster = Date.now();
        for (const target of getAssignmentTargets(id, result.data)) {
          try {
            const assignmentResponse = await apiService.authenticatedRequest(
              `/api/assignments/target/${target.targetType}/${encodeURIComponent(target.targetId)}?t=${cacheBuster}`,
              { method: 'GET' }
            );
            const assignmentPayload = (assignmentResponse as any).data ?? (assignmentResponse as any).Data;
            const assignmentData = assignmentArrayFrom(assignmentPayload);
            if (assignmentData.length > 0) {
              nextAssignments = mergeAssignmentRecords(nextAssignments, assignmentData);
            }
          } catch (assignmentError) {
            console.warn('[ContactEditForm] Failed to load contact assignments target:', target, assignmentError);
          }
        }
        if (nextAssignments.length > 0) {
          setContactAssignments(nextAssignments);
        }
      }
    } catch (error) {
      console.error('[ContactEditForm] Failed to load contact notes:', error);
    } finally {
      setLoadingNotes(false);
    }
  }, []);

  useEffect(() => {
    if (contact?.id) {
      loadNotes(contact.id);
    } else {
      setNotes([]);
      setContactAssignments([]);
    }
  }, [contact?.id, profileRefreshKey, loadNotes]);

  const loadContactDataLists = useCallback(async (contactId: string) => {
    setLoadingContactDataLists(true);
    try {
      const listsResponse = await apiService.getDataLists({ systemModule: 'contacts', limit: 1000 });
      const lists = listsResponse.statusCode === 1000 && Array.isArray(listsResponse.data) ? listsResponse.data : [];
      const membershipResults = await Promise.all(
        lists.map(async (list) => {
          try {
            const recordResponse = await apiService.getDataListRecord(list._id, contactId);
            return { list, isMember: recordResponse.statusCode === 1000 && !!recordResponse.data };
          } catch {
            return { list, isMember: false };
          }
        })
      );
      setContactDataLists(membershipResults.filter((item) => item.isMember).map((item) => item.list));
      setAvailableContactDataLists(membershipResults.filter((item) => !item.isMember).map((item) => item.list));
    } catch (error) {
      console.error('[ContactEditForm] Failed to load contact data list membership:', error);
      setContactDataLists([]);
      setAvailableContactDataLists([]);
    } finally {
      setLoadingContactDataLists(false);
    }
  }, []);

  useEffect(() => {
    if (contact?.id) {
      loadContactDataLists(contact.id);
    } else {
      setContactDataLists([]);
      setAvailableContactDataLists([]);
    }
  }, [contact?.id, profileRefreshKey, loadContactDataLists]);

  // Orders/Quotations — fetched once per contact (not on every re-render),
  // capped to a lightweight "most recent 3" preview so the contact pane
  // stays scannable rather than becoming a full history list.
  // mirroring loadNotes' own id-keyed effect above.
  useEffect(() => {
    if (!contact?.id) {
      setOrders([]);
      setQuotations([]);
      return;
    }
    const contactId = contact.id;
    setLoadingOrders(true);
    apiService.getOrders({ contactId, limit: 3 })
      .then((res) => setOrders(res.statusCode === 1000 && res.data ? res.data : []))
      .catch((error) => console.error('[ContactEditForm] Failed to load contact orders:', error))
      .finally(() => setLoadingOrders(false));

    setLoadingQuotations(true);
    apiService.getQuotations({ contactId, limit: 3 })
      .then((res) => setQuotations(res.statusCode === 1000 && res.data ? res.data.quotations : []))
      .catch((error) => console.error('[ContactEditForm] Failed to load contact quotations:', error))
      .finally(() => setLoadingQuotations(false));
  }, [contact?.id, profileRefreshKey]);

  const handleUpdateOrderField = async (orderId: string, field: 'orderStatus' | 'paymentStatus', value: string) => {
    setUpdatingCardAction(`${field}:${orderId}`);
    try {
      const response = await apiService.updateOrder(orderId, { [field]: value } as any);
      if (response.statusCode === 1000) {
        setOrders((prev) => prev.map((order) => order._id === orderId ? { ...order, [field]: value } : order));
        notifyProfileCardMutation();
        showNotification('success', t('contactEdit.cards.updated'), field === 'orderStatus' ? t('contactEdit.cards.orderStatusUpdated') : t('contactEdit.cards.paymentStatusUpdated'));
      } else {
        showNotification('error', t('contactEdit.cards.updateFailed'), response.errors?.[0]?.message || t('contactEdit.cards.orderUpdateFailed'));
      }
    } catch (error: any) {
      showNotification('error', t('contactEdit.cards.updateFailed'), error.message || t('contactEdit.cards.orderUpdateFailed'));
    } finally {
      setUpdatingCardAction(null);
      setCardActionSheet(null);
    }
  };

  const handleUpdateQuotationStatus = async (quotationId: string, quotationStatus: string) => {
    setUpdatingCardAction(`quotationStatus:${quotationId}`);
    try {
      const response = await apiService.updateQuotation(quotationId, { quotationStatus });
      if (response.statusCode === 1000) {
        setQuotations((prev) => prev.map((quotation) => quotation._id === quotationId ? { ...quotation, quotationStatus } : quotation));
        notifyProfileCardMutation();
        showNotification('success', t('contactEdit.cards.updated'), t('contactEdit.cards.quotationStatusUpdated'));
      } else {
        showNotification('error', t('contactEdit.cards.updateFailed'), response.errors?.[0]?.message || t('contactEdit.cards.quotationUpdateFailed'));
      }
    } catch (error: any) {
      showNotification('error', t('contactEdit.cards.updateFailed'), error.message || t('contactEdit.cards.quotationUpdateFailed'));
    } finally {
      setUpdatingCardAction(null);
      setCardActionSheet(null);
    }
  };

  const handleAssignmentAction = async (assignmentId: string, action: 'start' | 'complete' | 'cancel') => {
    setUpdatingCardAction(`assignment:${assignmentId}:${action}`);
    try {
      const response = await apiService.authenticatedRequest(`/api/assignments/${assignmentId}/${action}`, {
        method: 'POST',
        ...(action === 'complete' ? { body: JSON.stringify({ completionNotes: '' }) } : {}),
      });
      if (response.statusCode === 1000) {
        const nextStatus = action === 'start' ? 'inProgress' : action === 'complete' ? 'completed' : 'cancelled';
        setContactAssignments((prev) => prev.map((assignment) => assignment._id === assignmentId ? { ...assignment, status: nextStatus } : assignment));
        if (contact?.id) {
          loadNotes(contact.id);
        }
        notifyProfileCardMutation();
        showNotification('success', t('contactEdit.cards.updated'), t('contactEdit.cards.assignmentUpdated', { status: statusLabel('assignment', nextStatus) }));
      } else {
        showNotification('error', t('contactEdit.cards.updateFailed'), response.errors?.[0]?.message || t('contactEdit.cards.assignmentUpdateFailed'));
      }
    } catch (error: any) {
      showNotification('error', t('contactEdit.cards.updateFailed'), error.message || t('contactEdit.cards.assignmentUpdateFailed'));
    } finally {
      setUpdatingCardAction(null);
    }
  };

  const handleDataListMembershipChange = async (dataListId: string, action: 'add' | 'remove') => {
    if (!contact?.id || updatingDataListId) return;
    setUpdatingDataListId(dataListId);
    try {
      const response = action === 'add'
        ? await apiService.linkDataListRecords(dataListId, [contact.id])
        : await apiService.unlinkDataListRecords(dataListId, [contact.id]);
      if (response.statusCode === 1000) {
        await loadContactDataLists(contact.id);
        notifyProfileCardMutation();
        showNotification('success', action === 'add' ? 'Added to list' : 'Removed from list', action === 'add' ? `${name} was added.` : `${name} was removed.`);
      } else {
        showNotification('error', t('contactEdit.cards.listUpdateFailed'), response.errors?.[0]?.message || t('contactEdit.cards.listUpdateFailedMessage'));
      }
    } catch (error: any) {
      showNotification('error', t('contactEdit.cards.listUpdateFailed'), error.message || t('contactEdit.cards.listUpdateFailedMessage'));
    } finally {
      setUpdatingDataListId(null);
    }
  };

  const handleAddNote = useCallback(async (content: string) => {
    if (!contact?.id || !canManageNotes) return;
    try {
      const result = await apiService.addContactNote(contact.id, content);
      if (result.statusCode === 1000 && result.data) {
        setNotes(result.data.notes || []);
        notifyProfileCardMutation();
      } else {
        showNotification('error', t('contactEdit.errors.title'), t('contactEdit.errors.addNoteFailed'));
      }
    } catch (error) {
      console.error('[ContactEditForm] Failed to add note:', error);
      showNotification('error', t('contactEdit.errors.title'), t('contactEdit.errors.addNoteFailed'));
    }
  }, [contact?.id, canManageNotes, notifyProfileCardMutation, showNotification, t]);

  const handleDeleteNote = useCallback(async (noteId: string | number) => {
    if (!contact?.id || !canManageNotes) return;
    try {
      const result = await apiService.deleteContactNote(contact.id, String(noteId));
      if (result.statusCode === 1000 && result.data) {
        setNotes(result.data.notes || []);
        notifyProfileCardMutation();
      } else {
        showNotification('error', t('contactEdit.errors.title'), t('contactEdit.errors.deleteNoteFailed'));
      }
    } catch (error) {
      console.error('[ContactEditForm] Failed to delete note:', error);
      showNotification('error', t('contactEdit.errors.title'), t('contactEdit.errors.deleteNoteFailed'));
    }
  }, [contact?.id, canManageNotes, notifyProfileCardMutation, showNotification, t]);

  const getContactFormPrefill = useCallback(() => {
    if (!contact) return null;
    const parentContact = (contact as any).parentContact;
    const contactId = parentContact?._id || parentContact?.id || contact.id;
    const findIdentifier = (type: string) => channelIdentifiers.find((identifier) => {
      const value = identifier.value || '';
      return identifier.type === type && value && !value.includes('*') && !value.includes('•');
    });
    // Phone/WhatsApp identifiers hold the NATIONAL number (country code kept
    // separately for the country picker) — orders/quotations need the full
    // international number, as digits with the calling code (e.g.
    // 966501234567), the same shape their contact field stores; otherwise it
    // reads the first digits of the national number as a country code.
    const fullPhone = (identifier?: ChannelIdentifier) =>
      identifier ? formatIdentifierForOpen(identifier).replace(/[^\d]/g, '') : '';
    const phone = fullPhone(findIdentifier('phone')) || fullPhone(findIdentifier('whatsapp'))
      || String(contact.phoneNumber || parentContact?.profile?.phone || '').replace(/[^\d]/g, '');
    const email = findIdentifier('email')?.value || contact.email || parentContact?.profile?.email || '';
    const contactLocation = location || parentContact?.profile?.location;

    return {
      id: contactId,
      _id: contactId,
      name: name.trim() || contact.name || parentContact?.profile?.name || '',
      phoneNumber: phone,
      phone,
      email,
      address: contactLocation?.address || '',
      location: contactLocation || undefined,
      profilePictureUrl: avatar || contact.profilePictureUrl || parentContact?.profile?.avatar,
    };
  }, [avatar, channelIdentifiers, contact, location, name]);

  // visitId (string only — onPress passes an event) logs the created record on that visit.
  const handleAddOrderForContact = useCallback((visitId?: unknown) => {
    const prefillContact = getContactFormPrefill();
    navigateTo('order-add', undefined, prefillContact ? {
      prefillOrder: {
        source: 'contact-profile',
        contactId: prefillContact.id,
        customer: prefillContact,
        location: prefillContact.location,
        ...(typeof visitId === 'string' ? { visitId } : {}),
      },
    } : undefined);
  }, [getContactFormPrefill, navigateTo]);

  const handleAddVendorForContact = useCallback((visitId?: unknown) => {
    const prefillContact = getContactFormPrefill();
    navigateTo('vendor-create', undefined, prefillContact ? {
      prefillVendor: {
        contactId: prefillContact.id,
        ...(typeof visitId === 'string' ? { visitId } : {}),
        name: prefillContact.name,
        phone: prefillContact.phoneNumber,
        email: prefillContact.email,
        address: prefillContact.address,
        location: prefillContact.location,
      },
    } : undefined);
  }, [getContactFormPrefill, navigateTo]);

  const handleAddQuotationForContact = useCallback((visitId?: unknown) => {
    const prefillContact = getContactFormPrefill();
    navigateTo('quotation-create', undefined, prefillContact ? {
      prefillQuotation: {
        source: 'contact-profile',
        contactId: prefillContact.id,
        customer: prefillContact,
        location: prefillContact.location,
        ...(typeof visitId === 'string' ? { visitId } : {}),
      },
    } : undefined);
  }, [getContactFormPrefill, navigateTo]);

  // Upload avatar
  const handleAvatarUpload = async () => {
    if (!canManageContacts) return;
    try {
      const hasPermission = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!hasPermission.granted) {
        Alert.alert(t('contactEdit.permission.title'), t('contactEdit.permission.message'));
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        aspect: [1, 1],
        quality: 0.8,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        setAvatarUploading(true);
        
        // Upload avatar to storage
        // For new contacts, we'll upload after contact is created
        if (contact?.id) {
          const avatarUrl = await uploadContactFile(asset.uri, `avatar_${Date.now()}.jpg`, 'image/jpeg', contact.id, 'avatar');
          if (avatarUrl) {
            setAvatar(avatarUrl);
          }
        } else {
          // For new contacts, store the local URI temporarily
          // It will be uploaded after contact creation
          setAvatar(asset.uri);
        }
      }
    } catch (error: any) {
      console.error('Error uploading avatar:', error);
      Alert.alert(t('contactEdit.errors.title'), t('contactEdit.errors.avatarUpload'));
    } finally {
      setAvatarUploading(false);
    }
  };

  // Upload document attachment
  const handleDocumentUpload = async () => {
    if (!canManageContacts) return;
    try {
      const result = await DocumentPicker.getDocumentAsync({
        type: '*/*',
        copyToCacheDirectory: true,
      });

      if (!result.canceled && result.assets && result.assets.length > 0) {
        const asset = result.assets[0];
        setUploadingAttachment(true);
        
        if (!contact?.id) {
          Alert.alert(t('contactEdit.info.title'), t('contactEdit.info.saveContactFirst'));
          return;
        }

        const fileUrl = await uploadContactFile(
          asset.uri,
          asset.name || `document_${Date.now()}.pdf`,
          asset.mimeType || 'application/pdf',
          contact.id,
          'documents'
        );
        
        if (fileUrl) {
          const newAttachment: ContactAttachment = {
            name: asset.name || 'Document',
            type: asset.mimeType || 'application/pdf',
            size: asset.size || 0,
            url: fileUrl,
            uploadedAt: new Date(),
          };
          setAttachments([...attachments, newAttachment]);
          notifyProfileCardMutation();
        }
      }
    } catch (error: any) {
      console.error('Error uploading document:', error);
      Alert.alert(t('contactEdit.errors.title'), t('contactEdit.errors.documentUpload'));
    } finally {
      setUploadingAttachment(false);
    }
  };

  // Upload file to storage with path {businessId}/contacts/{contactId}
  const uploadContactFile = async (
    fileUri: string,
    fileName: string,
    mimeType: string,
    contactId: string,
    subfolder: 'avatar' | 'documents' = 'documents'
  ): Promise<string | null> => {
    try {
      if (!currentBusiness?._id) {
        throw new Error('Business ID is required');
      }

      // Use API service method
      const response = await apiService.uploadContactAttachment(contactId, fileUri, fileName, mimeType, subfolder);
      
      if (response.statusCode === 1000 && response.data?.url) {
        return response.data.url;
      } else {
        throw new Error(response.errors?.[0]?.message || 'Upload failed');
      }
    } catch (error: any) {
      console.error('Error uploading contact file:', error);
      throw error;
    }
  };

  // Remove a previously uploaded document attachment — persists the removal
  // server-side (not just local state), mirroring how upload persists it.
  const handleRemoveAttachment = async (attachment: ContactAttachment) => {
    if (!contact?.id || removingAttachmentUrl) return;
    setRemovingAttachmentUrl(attachment.url);
    try {
      const response = await apiService.removeContactAttachment(contact.id, attachment.url);
      if (response.statusCode === 1000) {
        setAttachments((prev) => prev.filter((a) => a.url !== attachment.url));
        notifyProfileCardMutation();
      } else {
        showNotification('error', t('contactEdit.errors.title'), response.errors?.[0]?.message || t('contactEdit.documents.deleteFailed'));
      }
    } catch (error: any) {
      showNotification('error', t('contactEdit.errors.title'), error.message || t('contactEdit.documents.deleteFailed'));
    } finally {
      setRemovingAttachmentUrl(null);
    }
  };

  // Add channel identifier — marked isFreshlyAdded so the row can offer
  // "Add for X" quick actions to sibling phone-based channels once a real
  // value is typed, but only this first time (see updateChannelIdentifier).
  const addChannelIdentifier = (type: ChannelIdentifier['type']) => {
    setChannelIdentifiers([...channelIdentifiers, { key: genIdentifierKey(), type, value: '', isFreshlyAdded: true }]);
  };

  // Update channel identifier
  const updateChannelIdentifier = (index: number, value: string) => {
    const updated = [...channelIdentifiers];
    updated[index] = { ...updated[index], value };
    setChannelIdentifiers(updated);
  };

  // Remove channel identifier
  const removeChannelIdentifier = (index: number) => {
    const removed = channelIdentifiers[index];
    if (removed && removed.value) {
      // A MASKED value (contains '*', contacts.viewMobileNumber not granted)
      // can never be matched by value server-side — the real string was
      // never sent to this client. Confirmed real production bug: removing
      // a masked phone/whatsapp identifier showed success but silently
      // persisted nothing, since this queue was skipped entirely for masked
      // rows and the backend's only removal path was a value match. Always
      // queue the removal now, additionally passing this row's position
      // WITHIN ITS OWN TYPE's array (matching identifiers.<type>[]'s actual
      // server-side indexing) so a masked row can still be identified and
      // removed by position when the value itself is unusable.
      const indexWithinType = channelIdentifiers
        .slice(0, index)
        .filter((id) => id.type === removed.type).length;
      removedIdentifiersRef.current.push({ type: removed.type, value: removed.value, index: indexWithinType });
    }
    setChannelIdentifiers(channelIdentifiers.filter((_, i) => i !== index));
  };

  // "Add for WhatsApp" / "Add for Phone" quick action — copies the source
  // row's number into a new row for the sibling phone-based channel. Offered
  // for ANY phone/whatsapp identifier missing its sibling, whether that row
  // was just typed this session or loaded from an already-saved contact. The
  // button at the SOURCE row then flips to "Undo" (tracked via
  // quickAddedKey), which removes that specific added row and restores the
  // "Add for X" button — this toggle is only available for the row just
  // added this way; once the user moves on (edits something else, saves),
  // it's just a normal identifier row removable via the regular × button
  // like any other.
  const addSiblingPhoneIdentifier = (sourceIndex: number, targetType: ChannelIdentifier['type']) => {
    const source = channelIdentifiers[sourceIndex];
    if (!source) return;
    // Only treat an existing row of this type as "the same sibling" (to
    // toggle/undo in place) when its VALUE also matches the source — a
    // contact can have several WhatsApp numbers, so a different phone
    // number must always get its OWN new sibling row, never overwrite an
    // unrelated one just because it happens to share the same type. This
    // was the real bug: adding a second phone and clicking "use for
    // WhatsApp" clobbered the first WhatsApp identifier instead of adding
    // a second one, since the previous check matched on type alone.
    const existingSiblingIndex = channelIdentifiers.findIndex(
      (id) => id.type === targetType && id.value === source.value
    );

    if (existingSiblingIndex !== -1) {
      // This exact number already has a sibling of this type (most likely
      // its own previously-added one) — toggle/restore it in place rather
      // than inserting a duplicate row. Its old value, if it was a real
      // saved one, still needs to be queued for removal server-side (same
      // as removeChannelIdentifier does), since this overwrites rather
      // than appends.
      const existingSibling = channelIdentifiers[existingSiblingIndex];
      const hadRealValue = !!existingSibling.value && !existingSibling.value.includes('*');
      if (hadRealValue) {
        removedIdentifiersRef.current.push({ type: existingSibling.type, value: existingSibling.value });
      }
      // Points at the existing sibling's OWN key (its key never changes here,
      // unlike the insert-new-row branch below which mints a fresh key for
      // the row it creates) — undoSiblingPhoneIdentifier looks the target row
      // up by this key to restore preQuickAddValue in place.
      const updated = [...channelIdentifiers];
      updated[sourceIndex] = { ...updated[sourceIndex], quickAddedKey: existingSibling.key };
      updated[existingSiblingIndex] = {
        ...existingSibling,
        quickAddedKey: undefined,
        // Only a REAL prior value needs restoring on undo — an empty row
        // being filled in for the first time has nothing to revert to, and
        // undo should just clear it back to empty in that case.
        preQuickAddValue: hadRealValue ? existingSibling.value : '',
        value: source.value,
        countryCode: source.countryCode,
        // The new value has no linkedChannelContacts entry yet (that's only
        // created on Save) — gates "Set Primary" below, which would
        // otherwise fail with "no matching linked identifier" until saved.
        isFreshlyAdded: true,
      };
      setChannelIdentifiers(updated);
      return;
    }

    const newKey = genIdentifierKey();
    const newIdentifier: ChannelIdentifier = {
      key: newKey,
      type: targetType,
      value: source.value,
      countryCode: source.countryCode,
      // No linkedChannelContacts entry exists for this brand-new row until
      // Save runs — gates "Set Primary" below, same reasoning as the
      // overwrite branch above.
      isFreshlyAdded: true,
    };
    const updated = [...channelIdentifiers];
    updated[sourceIndex] = { ...updated[sourceIndex], quickAddedKey: newKey };
    updated.splice(sourceIndex + 1, 0, newIdentifier);
    setChannelIdentifiers(updated);
  };

  // Reverses addSiblingPhoneIdentifier. Two cases, distinguished by whether
  // the target row carries preQuickAddValue:
  // - Replace case (an existing sibling's value was overwritten): restore
  //   its original value in place rather than deleting the row — it existed
  //   before the quick-add action, undo must not delete it. Also pops the
  //   entry addSiblingPhoneIdentifier pushed onto removedIdentifiersRef,
  //   since the "removal" of the old value never actually happens now.
  // - Insert case (a brand-new row was created): removes that row entirely
  //   (never queued in removedIdentifiersRef; it was never saved to begin
  //   with) and restores the "Add for X" button on the source row.
  const undoSiblingPhoneIdentifier = (sourceIndex: number) => {
    const source = channelIdentifiers[sourceIndex];
    if (!source?.quickAddedKey) return;
    const targetKey = source.quickAddedKey;
    const target = channelIdentifiers.find(id => id.key === targetKey);

    if (target && target.preQuickAddValue !== undefined) {
      if (target.preQuickAddValue) {
        const idx = removedIdentifiersRef.current.findIndex(
          (r) => r.type === target.type && r.value === target.preQuickAddValue
        );
        if (idx !== -1) removedIdentifiersRef.current.splice(idx, 1);
      }
      const updated = channelIdentifiers.map((id) => {
        if (id.key === targetKey) {
          return { ...id, value: target.preQuickAddValue as string, preQuickAddValue: undefined };
        }
        if (id.key === source.key) {
          return { ...id, quickAddedKey: undefined };
        }
        return id;
      });
      setChannelIdentifiers(updated);
      return;
    }

    const updated = channelIdentifiers
      .filter(id => id.key !== targetKey)
      .map(id => (id.key === source.key ? { ...id, quickAddedKey: undefined } : id));
    setChannelIdentifiers(updated);
  };

  // Marks one identifier as primary among all identifiers of the SAME type —
  // local state only here; the actual isPrimary flip on linkedChannelContacts
  // happens server-side via apiService.setPrimaryIdentifier on save-adjacent
  // action (see the button's onPress below), matching how tags/assignments in
  // this form call a dedicated endpoint rather than folding into handleSave.
  const setPrimaryIdentifier = async (index: number) => {
    const target = channelIdentifiers[index];
    if (!target || !contact?.id || target.value.includes('*')) return;
    try {
      const response = await apiService.setPrimaryContactIdentifier(contact.id, target.type, target.value);
      if (response.statusCode !== 1000) {
        throw new Error(response.errors?.[0]?.message || 'Failed to set primary identifier');
      }
      setChannelIdentifiers(channelIdentifiers.map((id, i) =>
        id.type === target.type ? { ...id, isPrimary: i === index } : id
      ));
    } catch (error: any) {
      console.error('Error setting primary identifier:', error);
      showNotification('error', t('contactEdit.errors.title'), error.message || t('contactEdit.errors.setPrimaryFailed'));
    }
  };

  // Opens a conversation on a channel icon tap — mirrors PhoneCallScreen.tsx's
  // handleChannelPress. Chat-capable channels are restricted to what
  // createOrFindConversation actually supports (whatsapp/facebook-messenger);
  // other icons still render but aren't tappable, same convention as the
  // call-end screen. Phone is its own case: a phone number isn't a chat
  // channel, so tapping it opens Call History scoped to this contact instead.
  //
  // onChannelSelect, when provided, takes over entirely for chat-capable
  // channels instead of startConversation — the master-detail pane flow
  // (Home/Contacts/Chat) wants the tapped channel's conversation to open in
  // the adjacent right pane, not navigate to a full conversation screen.
  const getPhoneIdentifierIndex = (identifier: ChannelIdentifier) => {
    if (identifier.type !== 'phone') return undefined;
    const phoneIdentifiers = channelIdentifiers.filter((item) => item.type === 'phone');
    const index = phoneIdentifiers.findIndex((item) => item.key === identifier.key);
    return index >= 0 ? index : undefined;
  };

  const handleChannelPress = (identifier: ChannelIdentifier) => {
    if (!contact?.id || !identifier.value.trim()) return;
    const openIdentifierValue = formatIdentifierForOpen(identifier);

    if (identifier.type === 'phone') {
      const phoneIndex = getPhoneIdentifierIndex(identifier);

      if (onViewCallHistory) {
        setRightPaneCollapsed(false);
        onViewCallHistory(contact.id, name, openIdentifierValue, phoneIndex);
      } else {
        navigateTo('call-history', undefined, {
          contactId: contact.id,
          contactName: name,
          phoneNumber: openIdentifierValue,
          phoneIndex,
        });
      }
      return;
    }

    if (!OPENABLE_CHANNELS.has(identifier.type)) return;

    if (onChannelSelect) {
      // The tapped channel's conversation renders in the adjacent pane, which
      // may have been collapsed by the user — force it back open so the tap
      // has a visible effect instead of silently updating a hidden pane.
      setRightPaneCollapsed(false);
      onChannelSelect(identifier.type, openIdentifierValue, identifier.channelContactId);
      return;
    }
    startConversation({
      id: contact.id,
      name,
      channel: identifier.type as 'whatsapp' | 'facebook-messenger',
      phoneNumber: identifier.type === 'whatsapp' ? openIdentifierValue : undefined,
    }, identifier.type === 'whatsapp' ? openIdentifierValue : undefined);
  };

  // Add tag
  const handleAddTag = () => {
    if (!canManageTags) return;
    if (newTag.trim() && !tags.includes(newTag.trim())) {
      setTags([...tags, newTag.trim()]);
      setNewTag('');
    }
  };

  // Remove tag
  const handleRemoveTag = (tagToRemove: string) => {
    if (!canManageTags) return;
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleResolveMapsUrl = async () => {
    if (!mapsUrlInput.trim() || resolvingMapsUrl) return;
    setResolvingMapsUrl(true);
    try {
      const response = await apiService.resolveContactLocationUrl(mapsUrlInput.trim());
      if (response.statusCode === 1000 && response.data) {
        setLocation({
          latitude: response.data.latitude,
          longitude: response.data.longitude,
          address: response.data.address,
          city: response.data.city,
          country: response.data.country,
        });
        setMapsUrlInput('');
        setShowMapsUrlInput(false);
        showNotification('success', t('contactEdit.location.linkResolved.title'), t('contactEdit.location.linkResolved.message'));
      } else {
        showNotification('error', t('contactEdit.location.linkFailed.title'), response.errors?.[0]?.message || t('contactEdit.location.linkFailed.message'));
      }
    } catch (error: any) {
      showNotification('error', t('contactEdit.location.linkFailed.title'), error.message || t('contactEdit.location.linkFailed.message'));
    } finally {
      setResolvingMapsUrl(false);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!canManageContacts) return;
    try {
      if (!name.trim()) {
        Alert.alert(t('contactEdit.validation.title'), t('contactEdit.validation.nameRequired'));
        return;
      }

      // Validate at least one identifier
      const validIdentifiers = channelIdentifiers.filter(id => id.value && id.value.trim());
      if (validIdentifiers.length === 0) {
        Alert.alert(t('contactEdit.validation.title'), t('contactEdit.validation.identifierRequired'));
        return;
      }

      setIsSaving(true);

      // Format identifiers as arrays for backend
      const identifiersObj: Record<string, string[]> = {};
      
      // Iterate through channelIdentifiers to match with refs correctly
      channelIdentifiers.forEach((id, index) => {
        if (!id.value || !id.value.trim()) {
          return; // Skip empty identifiers
        }

        // Still masked at save time ('*' only ever comes from maskPhoneValue,
        // never a real phone number) — the user never actually replaced it
        // with a real value, so it must NOT be sent to the backend. Without
        // this, PhoneInput's own digit-stripping on save turns
        // "*******6631" into exactly "6631", silently overwriting the real
        // stored number just because this form was opened (e.g. to add a
        // note) and saved. See maskedOnLoadRef's doc comment for the full
        // mechanism this closes.
        if ((id.type === 'phone' || id.type === 'whatsapp') && id.value.includes('*')) {
          return;
        }

        let identifierValue = id.value.trim();

        // For phone and whatsapp, recombine the country calling code with the
        // bare national number before saving. Confirmed real bug: this used
        // to rely ENTIRELY on a PhoneInputComponent ref's own getValue() —
        // when that ref was missing (not yet mounted/registered for this
        // row) or returned no formattedNumber/dialCode (observed on a row
        // the user never touched, e.g. opening the form and saving with no
        // edits), it silently fell through to the raw `id.value`, which
        // (per formatIdentifierForDisplay's own doc comment above) is
        // ALWAYS the bare national number — country code already stripped
        // at load time. That stripped value doesn't match the existing
        // stored identifier after the backend's dedup normalization, so
        // EVERY phone/whatsapp identifier got re-added as a "new" one on
        // every save, regardless of whether anything was actually edited.
        // id.countryCode is the SAME reliably-tracked field
        // formatIdentifierForDisplay already uses for this exact
        // recombination elsewhere in this file — preferred here as the
        // primary source instead of a ref that can silently be absent; the
        // original ref-based lookup is kept only as a fallback for the rare
        // case a row has no resolvable countryCode at all.
        if (id.type === 'phone' || id.type === 'whatsapp') {
          let recombined: string | null = null;
          if (id.countryCode) {
            const digitsOnly = identifierValue.replace(/\D/g, '');
            // parsePhoneNumber (libphonenumber-js) correctly strips the
            // national trunk prefix (e.g. the leading "0" in a Saudi
            // "0504553661") before combining with the country calling
            // code. The naive concatenation this replaced did not, and
            // produced corrupted identifiers like "9660504553661" instead
            // of "966504553661" — the exact bug behind the Sept 2026
            // duplicate-contact incident.
            try {
              const parsed = parsePhoneNumber(digitsOnly, id.countryCode as CountryCode);
              recombined = parsed.number.replace(/^\+/, '');
            } catch {
              // libphonenumber-js can throw for numbers it can't fully
              // parse (unusual lengths, etc.) — still strip a leading
              // trunk "0" before concatenating so this fallback can never
              // reproduce the same corruption.
              try {
                const callingCode = getCountryCallingCode(id.countryCode as CountryCode);
                recombined = `${callingCode}${digitsOnly.replace(/^0+/, '')}`;
              } catch {
                recombined = null;
              }
            }
          }

          if (recombined) {
            identifierValue = recombined;
          } else {
            const refKey = `${id.type}_${index}`;
            const phoneInputRef = phoneInputRefs.current[refKey];

            if (phoneInputRef) {
              try {
                const phoneData = phoneInputRef.getValue();
                if (phoneData?.formattedNumber) {
                  // Remove the "+" prefix before saving
                  identifierValue = phoneData.formattedNumber.replace(/^\+/, '');
                } else if (phoneData?.dialCode && identifierValue && !identifierValue.startsWith('+')) {
                  // Fallback: combine dial code with national number, then remove "+"
                  const combined = `${phoneData.dialCode}${identifierValue.replace(/\D/g, '')}`;
                  identifierValue = combined.replace(/^\+/, '');
                } else if (identifierValue.startsWith('+')) {
                  // If value already has "+", remove it
                  identifierValue = identifierValue.replace(/^\+/, '');
                }
              } catch (error) {
                console.warn('Failed to get formatted phone number from ref:', error);
                // Fallback to original value
              }
            }
          }
        }
        
        if (!identifiersObj[id.type]) {
          identifiersObj[id.type] = [];
        }
        if (identifierValue && !identifiersObj[id.type].includes(identifierValue)) {
          identifiersObj[id.type].push(identifierValue);
        }
      });

      // Determine primary identifier
      const primaryIdentifier = validIdentifiers[0].value.trim();
      const primaryIdentifierType = validIdentifiers[0].type === 'whatsapp' ? 'phone' : 
                                    validIdentifiers[0].type === 'facebook-messenger' ? 'facebook-messenger' :
                                    validIdentifiers[0].type === 'phone' ? 'phone' :
                                    validIdentifiers[0].type === 'email' ? 'email' : 'manual';

      if (contact?.id) {
        // Update existing parent contact
        // If avatar is a local URI (new upload), upload it first
        let finalAvatarUrl = avatar;
        if (avatar && (avatar.startsWith('file://') || avatar.startsWith('content://') || avatar.startsWith('ph://'))) {
          try {
            finalAvatarUrl = await uploadContactFile(avatar, `avatar_${Date.now()}.jpg`, 'image/jpeg', contact.id, 'avatar');
          } catch (error) {
            console.error('Error uploading avatar:', error);
            // Continue with update even if avatar upload fails
          }
        }

        const response = await apiService.updateParentContact(contact.id, {
          profile: {
            name: name.trim(),
            avatar: finalAvatarUrl || undefined,
            email: identifiersObj.email?.[0] || undefined,
            phone: identifiersObj.phone?.[0] || identifiersObj.whatsapp?.[0] || undefined,
            location: (location || nationality.trim()) ? { ...location, nationality: nationality.trim() || undefined } : undefined,
            tags: tags,
          },
          identifiers: identifiersObj,
          removedIdentifiers: removedIdentifiersRef.current,
        });

        if (response.statusCode === 1000 && response.data) {
          const updatedContact = {
            ...contact,
            name: name.trim(),
            phoneNumber: identifiersObj.phone?.[0] || identifiersObj.whatsapp?.[0],
            email: identifiersObj.email?.[0],
            profilePictureUrl: finalAvatarUrl,
            parentContact: response.data,
          } as Contact;

          if (onSave) {
            onSave(updatedContact);
          }

          notifyProfileCardMutation();
          showNotification('success', t('contactEdit.success.title'), t('contactEdit.success.updated'));
          if (onClose) {
            onClose();
          }
        } else {
          throw new Error(response.errors?.[0]?.message || 'Failed to update contact');
        }
      } else {
        // Create new parent contact first
        const response = await apiService.createParentContact({
          primaryIdentifier,
          primaryIdentifierType: primaryIdentifierType as any,
          identifiers: identifiersObj,
          profile: {
            name: name.trim(),
            avatar: undefined, // Will upload after creation
            email: identifiersObj.email?.[0] || undefined,
            phone: identifiersObj.phone?.[0] || identifiersObj.whatsapp?.[0] || undefined,
            location: (location || nationality.trim()) ? { ...location, nationality: nationality.trim() || undefined } : undefined,
            tags: tags,
          },
        });

        if (response.statusCode === 1000 && response.data) {
          const newContactId = response.data._id || response.data.id;
          
          // Upload avatar if provided (local URI)
          let finalAvatarUrl = avatar;
          if (avatar && (avatar.startsWith('file://') || avatar.startsWith('content://') || avatar.startsWith('ph://') || avatar.startsWith('blob:'))) {
            try {
              finalAvatarUrl = await uploadContactFile(avatar, `avatar_${Date.now()}.jpg`, 'image/jpeg', newContactId, 'avatar');
              // Update contact with avatar URL
              await apiService.updateParentContact(newContactId, {
                profile: {
                  avatar: finalAvatarUrl || undefined,
                },
              });
            } catch (error) {
              console.error('Error uploading avatar:', error);
              // Continue even if avatar upload fails
            }
          }

          const newContact = {
            id: newContactId,
            name: name.trim(),
            phoneNumber: identifiersObj.phone?.[0] || identifiersObj.whatsapp?.[0],
            email: identifiersObj.email?.[0],
            profilePictureUrl: finalAvatarUrl || undefined,
            channel: identifiersObj.whatsapp?.[0] ? 'whatsapp' : 'email',
            parentContact: response.data,
          } as Contact;

          if (onSave) {
            onSave(newContact);
          }

          notifyProfileCardMutation();
          showNotification('success', t('contactEdit.success.title'), t('contactEdit.success.created'));
          if (onClose) {
            onClose();
          }
        } else {
          throw new Error(response.errors?.[0]?.message || 'Failed to create contact');
        }
      }
    } catch (error: any) {
      console.error('❌ [ContactEditForm] Error saving contact:', error);
      Alert.alert(t('contactEdit.errors.title'), error.message || t('contactEdit.errors.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  // Handle delete — opens the confirmation modal instead of Alert.alert,
  // since Alert.alert renders nothing on web (no native dialog there),
  // which meant delete had no real confirmation step on that platform.
  const handleDelete = () => {
    if (!contact || !canDeleteContacts) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!contact) return;
    try {
      setIsDeleting(true);

      // Confirmed real production bug: this previously never called any delete
      // endpoint at all (a stubbed TODO) — it just showed a success toast and
      // called onDelete() to close the modal, so "deleting" a contact silently
      // did nothing and the contact reappeared on the next list refresh.
      const response = await apiService.deleteContact(contact.id);
      if (response.statusCode !== 1000) {
        throw new Error(response.errors?.[0]?.message || t('contactEdit.delete.failed'));
      }

      if (onDelete) {
        onDelete(contact.id);
      }

      setShowDeleteConfirm(false);

      if (onClose) {
        onClose();
      }

      showNotification('success', t('contactEdit.success.title'), t('contactEdit.delete.success'));
    } catch (error: any) {
      console.error('❌ [ContactEditForm] Error deleting contact:', error);
      showNotification('error', t('contactEdit.errors.title'), error.message || t('contactEdit.delete.failed'));
    } finally {
      setIsDeleting(false);
    }
  };

  const isNewContact = !contact;

  // "+" add-identifier menu: rendered in a transparent Modal anchored under
  // the button. It used to be an absolutely-positioned box INSIDE the
  // identifiers card on native (clipped by the card / covered by the cards
  // after it, so it never showed or couldn't be tapped) and a fixed-position
  // web overlay placed by a hardcoded 180px header guess (wrong in panes and
  // on phones). A Modal sits above everything on every platform.
  const [identifierMenuAnchor, setIdentifierMenuAnchor] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const openIdentifierMenu = () => {
    const node = addButtonRef.current as any;
    if (node?.measureInWindow) {
      node.measureInWindow((x: number, y: number, width: number, height: number) => {
        setIdentifierMenuAnchor({ x, y, width, height });
        setShowIdentifierMenu(true);
      });
    } else {
      setIdentifierMenuAnchor(null);
      setShowIdentifierMenu(true);
    }
  };
  const closeIdentifierMenu = () => {
    setShowIdentifierMenu(false);
    setMenuPosition(null);
  };
  const IDENTIFIER_MENU_ITEMS: Array<{ type: ChannelIdentifier['type']; icon: keyof typeof Ionicons.glyphMap; color: string; labelKey: string }> = [
    { type: 'phone', icon: 'call', color: colors.primary as string, labelKey: 'contactEdit.channel.phone' },
    { type: 'email', icon: 'mail', color: colors.primary as string, labelKey: 'contactEdit.channel.email' },
    { type: 'whatsapp', icon: 'logo-whatsapp', color: '#25D366', labelKey: 'contactEdit.channel.whatsapp' },
    { type: 'facebook-messenger', icon: 'logo-facebook', color: '#1877F2', labelKey: 'contactEdit.channel.facebookMessenger' },
    { type: 'instagram', icon: 'logo-instagram', color: '#E4405F', labelKey: 'contactEdit.channel.instagram' },
    { type: 'telegram', icon: 'paper-plane', color: '#0088CC', labelKey: 'contactEdit.channel.telegram' },
  ];

  const visibleChannelIdentifiers = React.useMemo(
    () => dedupeChannelIdentifiers(channelIdentifiers),
    [channelIdentifiers]
  );

  // Channel icons — tap to open a conversation on that channel (whatsapp/
  // facebook-messenger only, matching what createOrFindConversation actually
  // supports; phone opens Call History instead; other icons still show but
  // aren't tappable). The pencil toggles the compact editor below for
  // adding/editing/removing raw values, since editing is no longer what
  // tapping an icon does.
  //
  // ONE icon per TYPE, not one per raw entry — a contact with two WhatsApp
  // numbers previously showed two identical WhatsApp icons side by side. A
  // badge shows when a type has more than one entry; tapping it opens a
  // picker to choose which one to act on, mirroring ContactsList.tsx's
  // contact-card icon pickers (call/chat) rather than duplicating the icon
  // per value.
  //
  // Extracted to a variable (rather than inlined once) so compact mode can
  // place it directly beside the avatar/name as one grouped unit, while
  // non-compact keeps it as its own full-width row below — same content,
  // different position.
  const channelsRowContent = (
    <View style={[styles.channelsRow, compact && styles.channelsRowCompact, { flexDirection: rtl.rowDirection }]}>
      {Object.entries(
        visibleChannelIdentifiers
          .filter((identifier) => !!identifier.value.trim())
          .reduce((acc: Record<string, ChannelIdentifier[]>, identifier) => {
            (acc[identifier.type] = acc[identifier.type] || []).push(identifier);
            return acc;
          }, {})
      ).map(([type, group]) => {
        const identifier = group[0];
        const hasMultiple = group.length > 1;
        const openable = (OPENABLE_CHANNELS.has(type) || type === 'phone') && !!contact?.id;
        return (
          <TouchableOpacity
            key={type}
            style={[
              styles.channelIconWrap,
              compact && styles.channelIconWrapCompact,
              { backgroundColor: (getChannelColor(type) as string) + '20' },
            ]}
            onPress={(e) => {
              if (hasMultiple) {
                const { pageX, pageY } = (e.nativeEvent as any) ?? {};
                if (typeof pageX === 'number' && typeof pageY === 'number') {
                  setChannelIconPickerPosition(toWindowRelative(pageX, pageY));
                }
                setChannelIconPickerType(type as ChannelIdentifier['type']);
              } else {
                handleChannelPress(identifier);
              }
            }}
            disabled={!openable && !hasMultiple}
            activeOpacity={openable || hasMultiple ? 0.7 : 1}
          >
            <Ionicons name={getChannelIcon(type)} size={compact ? 18 : 20} color={getChannelColor(type)} />
            {hasMultiple && (
              <View style={styles.channelIconMultiBadge}>
                <Ionicons name="chevron-down" size={8} color={colors.white} />
              </View>
            )}
          </TouchableOpacity>
        );
      })}
      <View ref={addButtonRef} style={[styles.addButtonContainer, showIdentifierMenu && styles.addButtonContainerOpen]}>
        <TouchableOpacity
          style={[styles.channelIconWrap, compact && styles.channelIconWrapCompact, styles.channelsEditButton]}
          onPress={() => setShowChannelsEditor((v) => !v)}
          activeOpacity={0.7}
        >
          <Ionicons name="pencil" size={compact ? 15 : 16} color={colors.text.secondary} />
        </TouchableOpacity>
      </View>
    </View>
  );

  // Static map preview for the Location thumb. Use a keyless OSM static
  // image endpoint here: the old Mapbox URL depended on a stale public token
  // and rendered as a blank grey tile in the contact pane.
  const locationMapImageUrl = (location?.latitude != null && location?.longitude != null)
    ? `https://staticmap.openstreetmap.de/staticmap.php?center=${location.latitude},${location.longitude}&zoom=15&size=420x420&markers=${location.latitude},${location.longitude},red-pushpin`
    : null;
  const locationMapEmbedUrl = (location?.latitude != null && location?.longitude != null)
    ? (() => {
        const lat = location.latitude;
        const lng = location.longitude;
        const delta = 0.006;
        return `https://www.openstreetmap.org/export/embed.html?bbox=${lng - delta},${lat - delta},${lng + delta},${lat + delta}&layer=mapnik&marker=${lat},${lng}`;
      })()
    : null;

  // Save/Delete (+ Reminder/Add to List/Assign in compact mode) — extracted
  // so compact mode can place it on the far side of the avatar/name/channels
  // row (no separate header bar there, see below) while non-compact keeps it
  // in its own PaneHeader row exactly as before.
  const quickActionsNode = compact && !isNewContact && contact?.id ? (
        <ContactQuickActions
          contactId={contact.id}
          contactName={contact.name}
          phoneNumber={contact.phoneNumber}
          onNotify={showNotification}
          onListMembershipChange={() => {
            loadContactDataLists(contact.id);
            notifyProfileCardMutation();
          }}
          onAssignmentChange={() => {
            loadNotes(contact.id);
            notifyProfileCardMutation();
          }}
          compact
        />
  ) : null;

  const saveDeleteNode = (
    <>
      {canManageContacts && (
        compact ? (
          <TouchableOpacity
            style={styles.topActionSaveIconButton}
            onPress={handleSave}
            disabled={isSaving}
            hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          >
            {isSaving ? (
              <ActivityIndicator size="small" color={colors.white} />
            ) : (
              <>
                <Ionicons name="save-outline" size={15} color={colors.white} />
                <Text style={styles.topActionSaveIconButtonText}>{t('contactEdit.action.save')}</Text>
              </>
            )}
          </TouchableOpacity>
        ) : (
          <Button
            title={isSaving ? t('contactEdit.action.saving') : isNewContact ? t('contactEdit.action.create') : t('contactEdit.action.save')}
            onPress={handleSave}
            disabled={isSaving}
            loading={isSaving}
            style={styles.topActionSaveButton}
          />
        )
      )}
      {!isNewContact && canDeleteContacts && (
        <TouchableOpacity
          style={[styles.topActionDeleteButton, compact && styles.topActionDeleteButtonCompact]}
          onPress={handleDelete}
          disabled={isDeleting}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
        >
          <Ionicons name="trash-outline" size={compact ? 16 : 20} color="#EF4444" />
        </TouchableOpacity>
      )}
    </>
  );

  const formActionsCluster = (
    <>
      {quickActionsNode}
      {saveDeleteNode}
    </>
  );

  // Translated status chip/menu label — falls back to a humanized English
  // label for any status value the translations don't cover.
  const statusLabel = (group: 'order' | 'payment' | 'quotation' | 'assignment', status?: string) => {
    if (!status) return t('contactEdit.status.none');
    const key = `contactEdit.status.${group}.${status}`;
    const translated = t(key as any);
    return translated === key ? formatStatusLabel(status) : translated;
  };

  const copyCardNumber = async (value: string, label: string) => {
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.clipboard) {
        await navigator.clipboard.writeText(value);
      } else {
        await Clipboard.setStringAsync(value);
      }
      showNotification('success', t('contactEdit.cards.copied'), t('contactEdit.cards.copiedMessage', { label }));
    } catch (error) {
      console.error(`[ContactEditForm] Failed to copy ${label.toLowerCase()}:`, error);
      showNotification('error', t('contactEdit.errors.title'), t('contactEdit.cards.copyFailed'));
    }
  };

  return (
    <View style={[styles.container, compact && styles.compactContainer]}>
      {/* Header — non-compact (native full-screen) only. Compact (pane) mode
          has no separate header bar at all: Save/Delete/quick-actions move
          onto the far side of the avatar/name/channels row instead (see
          avatarActionsCluster below), and there's no close button in that
          context — the pane itself owns navigating away from a contact. */}
      {!compact && (
        <PaneHeader
          leading={onClose ? <PaneHeaderIconButton icon="close" onPress={onClose} size={24} /> : undefined}
          trailing={formActionsCluster}
        />
      )}
      {/* Compact profile on a phone (full-screen, not a desktop pane): there
          is no surrounding pane to navigate away with, and a single
          avatar+name+channels+actions row overflows — so a top bar carries
          back + Save/Delete, and the quick actions get their own row below
          the identity block. */}
      {compact && isPhoneWidth && (
        <PaneHeader
          leading={onClose ? <PaneHeaderIconButton icon={rtl.mirrorIcon('chevron-back') as any} onPress={onClose} size={24} /> : undefined}
          trailing={saveDeleteNode}
        />
      )}

      <ScrollView
        style={[styles.content, compact && styles.compactContent, !compact && phoneFormGutter]}
        showsVerticalScrollIndicator={false}
        onScrollBeginDrag={() => {
          setShowIdentifierMenu(false);
          setMenuPosition(null);
        }}
      >
        {/* Contact Avatar — compact mode (the master-detail pane flow on
            Home/Contacts/Chat) groups avatar+name+channels as ONE unit: the
            avatar sits beside a right column holding name-over-channels, and
            the avatar is sized (via identityColumnHeight, measured from that
            column's actual onLayout height) to match that column's combined
            height exactly, rather than just the name row. Non-compact mode
            is untouched — avatar/photo-button/name stacked, channels row
            below as its own full-width row, same as always. Other callers
            (native full-screen create/edit, the two add-contact modals,
            ConversationScreen's own "View Contact") don't pass compact. */}
        <View style={[styles.avatarSection, compact && styles.avatarSectionCompactGroup]}>
          {compact ? (
            <>
            <View style={[styles.avatarSectionCompactRow, { flexDirection: rtl.rowDirection }]}>
              <View
                style={[
                  styles.avatarContainer,
                  styles.avatarContainerCompact,
                  rtl.marginEnd(spacing.md),
                  identityColumnHeight ? { width: identityColumnHeight, height: identityColumnHeight } : null,
                ]}
              >
                {avatar ? (
                  <Image
                    source={{ uri: getImageUrlSync(avatar, apiBaseURL) }}
                    style={[
                      styles.avatarImageCompact,
                      identityColumnHeight ? { width: identityColumnHeight, height: identityColumnHeight, borderRadius: identityColumnHeight / 2 } : null,
                    ] as any}
                    onError={(e) => {
                      console.error('❌ [ContactEditForm] Failed to load avatar:', avatar, e.nativeEvent.error);
                    }}
                  />
                ) : (
                  <View
                    style={[
                      styles.avatarCompact,
                      identityColumnHeight ? { width: identityColumnHeight, height: identityColumnHeight, borderRadius: identityColumnHeight / 2 } : null,
                    ]}
                  >
                    <Text style={styles.avatarTextCompact}>
                      {name ? name.split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase() : '?'}
                    </Text>
                  </View>
                )}
                {avatarUploading && (
                  <View style={[styles.avatarOverlay, { borderRadius: (identityColumnHeight ?? 56) / 2 }]}>
                    <ActivityIndicator size="small" color={colors.white} />
                  </View>
                )}
                {/* Camera button overlaid on the (smaller) avatar itself
                    instead of a separate labeled button below it — there
                    isn't room for both an inline avatar AND a full-width
                    button in a narrow pane. */}
                {canManageContacts && (
                  <TouchableOpacity
                    style={styles.changePhotoButtonCompact}
                    onPress={handleAvatarUpload}
                    disabled={avatarUploading}
                  >
                    <Ionicons name="camera" size={12} color={colors.white} />
                  </TouchableOpacity>
                )}
              </View>
              {/* Name + channels, grouped as one column whose rendered
                  height drives the avatar's size above. */}
              <View
                style={styles.nameInputCompactWrap}
                onLayout={(e) => setIdentityColumnHeight(Math.min(72, Math.max(52, Math.round(e.nativeEvent.layout.height))))}
              >
                <Input
                  value={name}
                  onChangeText={setName}
                  placeholder={t('contactEdit.field.namePlaceholder')}
                  inputStyle={[styles.nameInputCompact, { textAlign: rtl.textStart }]}
                  containerStyle={styles.nameInputContainerCompact}
                />
                {channelsRowContent}
              </View>
              {!isPhoneWidth && (
                <View style={[styles.avatarActionsCluster, { flexDirection: rtl.rowDirection }]}>
                  {formActionsCluster}
                </View>
              )}
            </View>
            {isPhoneWidth && quickActionsNode && (
              <View style={[styles.phoneQuickActionsRow, { flexDirection: rtl.rowDirection }]}>
                {quickActionsNode}
              </View>
            )}
            </>
          ) : (
            <>
              <View style={styles.avatarContainer}>
                {avatar ? (
                  <Image
                    source={{ uri: getImageUrlSync(avatar, apiBaseURL) }}
                    style={styles.avatarImage}
                    onError={(e) => {
                      console.error('❌ [ContactEditForm] Failed to load avatar:', avatar, e.nativeEvent.error);
                    }}
                  />
                ) : (
                  <View style={styles.avatar}>
                    <Text style={styles.avatarText}>
                      {name ? name.split(' ').map(n => n.charAt(0)).join('').substring(0, 2).toUpperCase() : '?'}
                    </Text>
                  </View>
                )}
                {avatarUploading && (
                  <View style={styles.avatarOverlay}>
                    <ActivityIndicator size="small" color={colors.white} />
                  </View>
                )}
              </View>
              {canManageContacts && (
                <TouchableOpacity
                  style={[styles.changePhotoButton, { flexDirection: rtl.rowDirection }]}
                  onPress={handleAvatarUpload}
                  disabled={avatarUploading}
                >
                  <Ionicons name="camera" size={20} color={colors.primary} />
                  <Text style={styles.changePhotoText}>
                    {avatar ? t('contactEdit.photo.change') : t('contactEdit.photo.add')}
                  </Text>
                </TouchableOpacity>
              )}

              {/* Name — merged in from the old separate "Basic Information"
                  section so the whole identity block (avatar/name/channels/
                  quick actions) reads as one compact unit at the top. */}
              <Input
                value={name}
                onChangeText={setName}
                placeholder={t('contactEdit.field.namePlaceholder')}
                inputStyle={styles.nameInput}
                containerStyle={styles.nameInputContainer}
              />

              {channelsRowContent}
            </>
          )}
        </View>

        {/* Quick actions (Reminder / Add to List / Assign) — same row shown
            on the call-end screen (PhoneCallScreen.tsx), inlined right below
            the identity block, existing contacts only since they act on a
            real contactId. Compact mode moves this into the top header bar
            instead (alongside Save/Delete), so it isn't shown twice here. */}
        {!compact && !isNewContact && contact?.id && (
          <View style={styles.formSection}>
            <ContactQuickActions
              contactId={contact.id}
              contactName={contact.name}
              phoneNumber={contact.phoneNumber}
              onNotify={showNotification}
              onListMembershipChange={() => {
                loadContactDataLists(contact.id);
                notifyProfileCardMutation();
              }}
              onAssignmentChange={() => {
                loadNotes(contact.id);
                notifyProfileCardMutation();
              }}
            />
          </View>
        )}

        {/* Visit being worked with this contact — the whole visit flow
            (directions, rules, start, actions, end with outcome) happens
            here, inside the profile. */}
        {!isNewContact && contact?.id && activeVisitId && (
          <View style={styles.formSection}>
            <ContactVisitWorkspace
              visitId={activeVisitId}
              contactId={(contact as any).parentContact?._id || contact.id}
              contactName={name.trim() || contact.name || ''}
              phoneNumber={contact.phoneNumber}
              onClose={(ended) => {
                setActiveVisitId(null);
                onVisitClosed?.(ended);
              }}
              onChanged={() => {
                loadNotes(contact.id);
                loadContactDataLists(contact.id);
                setVisitsRefresh((n) => n + 1);
                notifyProfileCardMutation();
              }}
              onAddOrder={handleAddOrderForContact}
              onAddQuotation={handleAddQuotationForContact}
              onAddVendor={handleAddVendorForContact}
              linkedVendor={linkedVendor}
            />
          </View>
        )}

        {/* Vendor link — this contact as a supplier ("Add as vendor"). */}
        {!isNewContact && contact?.id && (
          <View style={[styles.formSection, { flexDirection: rtl.rowDirection, alignItems: 'center', gap: spacing.sm }]}>
            <Ionicons name="storefront-outline" size={16} color={colors.primary as string} />
            {linkedVendor ? (
              <TouchableOpacity onPress={() => navigateTo('vendor-detail', linkedVendor.id)} style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, color: colors.text.primary as string, textAlign: rtl.textStart }} numberOfLines={1}>
                  {t('contactEdit.vendor.linked', { name: linkedVendor.name })} <Text style={{ color: colors.primary as string }}>{t('contactEdit.vendor.view')}</Text>
                </Text>
              </TouchableOpacity>
            ) : (
              <TouchableOpacity onPress={() => handleAddVendorForContact()} style={{ flex: 1 }}>
                <Text style={{ fontSize: 13, color: colors.primary as string, textAlign: rtl.textStart }}>{t('contactEdit.vendor.add')}</Text>
              </TouchableOpacity>
            )}
          </View>
        )}

        {/* Assignment / Lists / Orders / Quotations — inline in compact pane mode, but
            as a horizontal strip with a usable card width instead of three
            squeezed columns. Each card remains a recent-3 preview. */}
        {!isNewContact && contact?.id && (
          <View style={styles.formSection}>
            <View
              style={[
                compact && !isPhonePane ? styles.profileCardsGridContent : styles.profileCardsStack,
                compact && !isPhonePane && { flexDirection: rtl.rowDirection },
              ]}
            >
            <ProfileCardsStrip
              {...(isPhonePane
                ? {
                    horizontal: true,
                    showsHorizontalScrollIndicator: false,
                    contentContainerStyle: [styles.profileCardsStripContent, { flexDirection: rtl.rowDirection }],
                  }
                : {})}
            >
            {/* Assignment — same underlying data as ContactsList.tsx's
                assignment badge, surfaced here as its own card instead of
                only a quick-action button. */}
            <View style={[styles.profileCard, compact && styles.profileCardInline, phoneCardWidth ? { width: phoneCardWidth } : null]}>
              <View style={[styles.profileCardHeader, { flexDirection: rtl.rowDirection }]}>
                <Text style={[styles.profileCardTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{t('contactEdit.section.assignment')}</Text>
                {canManageContacts && (
                  <TouchableOpacity onPress={() => setShowAssignmentModal(true)}>
                    <Text style={styles.profileCardAction}>{t('contactEdit.assignment.reassign')}</Text>
                  </TouchableOpacity>
                )}
              </View>
              {contactAssignments.length > 0 ? (
                <ScrollView
                  style={styles.assignmentListScroll}
                  nestedScrollEnabled
                  showsVerticalScrollIndicator={contactAssignments.length > 2}
                >
                  {sortAssignmentsForProfile(contactAssignments).map((assignment, index) => {
                    const assignedTo = assignment.assignedTo as any;
                    const department = assignment.departmentId as any;
                    const hasUserAssignment = !!assignedTo;
                    const assignedUserId = typeof assignedTo === 'string' ? assignedTo : assignedTo?._id;
                    const assignedDepartmentId = typeof department === 'string' ? department : department?._id;
                    const assignedUserKey = assignedUserId ? String(assignedUserId) : '';
                    const assignedDepartmentKey = assignedDepartmentId ? String(assignedDepartmentId) : '';
                    const resolvedUser = assignedUserKey
                      ? businessUsers.find((businessUser: any) => String(businessUser._id || businessUser.id) === assignedUserKey)
                      : null;
                    const resolvedDepartment = assignedDepartmentKey
                      ? businessDepartments.find((businessDepartment: any) => String(businessDepartment._id || businessDepartment.id) === assignedDepartmentKey)
                      : null;
                    const label = hasUserAssignment
                      ? resolvedUser
                        ? `${resolvedUser.firstName || ''} ${resolvedUser.lastName || ''}`.trim() || resolvedUser.email || 'User'
                        : typeof assignedTo === 'string'
                        ? 'Assigned user'
                        : `${assignedTo.firstName || ''} ${assignedTo.lastName || ''}`.trim() || assignedTo.email || assignedTo.name || 'Assigned user'
                      : typeof department === 'string'
                      ? resolvedDepartment?.name || 'Assigned department'
                      : department?.name || 'Assigned department';
                    const status = assignment.status || 'active';
                    const color = getAssignmentStatusColor(status);
                    const loadingKey = assignment._id ? `assignment:${assignment._id}:` : '';
                    const assignedDate = formatAssignmentDate(assignment.assignedAt || assignment.createdAt || assignment.updatedAt);
                    return (
                      <View key={assignment._id || `${label}-${index}`} style={[styles.profileCardItem, index === 0 && styles.profileCardRowFirst]}>
                        <View style={[styles.profileCardItemTop, { flexDirection: rtl.rowDirection }]}>
                          <View style={[styles.profileCardIcon, { backgroundColor: `${color}18` }]}>
                            <Ionicons name={hasUserAssignment ? 'person-outline' : 'business-outline'} size={15} color={color} />
                          </View>
                          <View style={styles.attachmentInfo}>
                            <Text style={[styles.profileCardRowTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{label}</Text>
                            <Text style={[styles.profileCardRowSubtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
                              {assignedDate}
                            </Text>
                          </View>
                          <View style={[styles.statusChip, { backgroundColor: `${color}18` }]}>
                            <Text style={[styles.statusChipText, { color }]}>{statusLabel('assignment', status)}</Text>
                          </View>
                        </View>
                        {!!assignment._id && (
                          <View style={[styles.profileCardActions, { flexDirection: rtl.rowDirection }]}>
                            {status === 'active' && (
                              <TouchableOpacity
                                style={styles.profileCardActionButton}
                                onPress={() => handleAssignmentAction(assignment._id!, 'start')}
                                disabled={updatingCardAction?.startsWith(loadingKey)}
                              >
                                <Ionicons name="play-outline" size={14} color={colors.primary} />
                                <Text style={styles.profileCardActionButtonText}>{t('contactEdit.cards.start')}</Text>
                              </TouchableOpacity>
                            )}
                            {(status === 'active' || status === 'inProgress') && (
                              <TouchableOpacity
                                style={styles.profileCardActionButton}
                                onPress={() => handleAssignmentAction(assignment._id!, 'complete')}
                                disabled={updatingCardAction?.startsWith(loadingKey)}
                              >
                                <Ionicons name="checkmark-outline" size={14} color={colors.primary} />
                                <Text style={styles.profileCardActionButtonText}>{t('contactEdit.cards.complete')}</Text>
                              </TouchableOpacity>
                            )}
                            {(status === 'active' || status === 'inProgress') && (
                              <TouchableOpacity
                                style={[styles.profileCardActionButton, styles.profileCardDangerAction]}
                                onPress={() => handleAssignmentAction(assignment._id!, 'cancel')}
                                disabled={updatingCardAction?.startsWith(loadingKey)}
                              >
                                <Ionicons name="close-outline" size={14} color="#EF4444" />
                                <Text style={[styles.profileCardActionButtonText, { color: '#EF4444' }]}>{t('common.cancel')}</Text>
                              </TouchableOpacity>
                            )}
                          </View>
                        )}
                      </View>
                    );
                  })}
                </ScrollView>
              ) : (
                <Text style={styles.profileCardEmpty}>{t('contactEdit.assignment.unassigned')}</Text>
              )}
            </View>

            <View style={[styles.profileCard, compact && styles.profileCardInline, phoneCardWidth ? { width: phoneCardWidth } : null]}>
              <View style={[styles.profileCardHeader, { flexDirection: rtl.rowDirection }]}>
                <Text style={[styles.profileCardTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{t('contactEdit.cards.lists')}</Text>
                {loadingContactDataLists && <ActivityIndicator size="small" color={colors.primary} />}
              </View>

              <View style={styles.listMembershipSection}>
                <Text style={[styles.profileCardSectionLabel, { textAlign: rtl.textStart }]}>{t('contactEdit.cards.listsAdded')}</Text>
                {contactDataLists.length > 0 ? (
                  <ScrollView style={styles.listMembershipScroll} nestedScrollEnabled showsVerticalScrollIndicator={contactDataLists.length > 3}>
                    {contactDataLists.map((list, index) => (
                      <View key={list._id} style={[styles.profileCardRow, index === 0 && styles.profileCardRowFirst, { flexDirection: rtl.rowDirection }]}>
                        <View style={[styles.profileCardIcon, { backgroundColor: `${colors.primary as string}14` }]}>
                          <Ionicons name="list-outline" size={15} color={colors.primary} />
                        </View>
                        <View style={styles.attachmentInfo}>
                          <Text style={[styles.profileCardRowTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{list.name}</Text>
                          {!!list.description && (
                            <Text style={[styles.profileCardRowSubtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{list.description}</Text>
                          )}
                        </View>
                        {canManageContacts && (
                          <TouchableOpacity
                            style={[styles.profileCardIconButton, styles.profileCardDangerIconButton]}
                            onPress={() => handleDataListMembershipChange(list._id, 'remove')}
                            disabled={updatingDataListId === list._id}
                          >
                            {updatingDataListId === list._id ? (
                              <ActivityIndicator size="small" color="#EF4444" />
                            ) : (
                              <Ionicons name="trash-outline" size={15} color="#EF4444" />
                            )}
                          </TouchableOpacity>
                        )}
                      </View>
                    ))}
                  </ScrollView>
                ) : (
                  !loadingContactDataLists && <Text style={styles.profileCardEmpty}>{t('contactEdit.cards.listsEmpty')}</Text>
                )}
              </View>

              <View style={styles.listMembershipSection}>
                <Text style={[styles.profileCardSectionLabel, { textAlign: rtl.textStart }]}>{t('contactEdit.cards.listsAvailable')}</Text>
                {availableContactDataLists.length > 0 ? (
                  <ScrollView style={styles.listMembershipScroll} nestedScrollEnabled showsVerticalScrollIndicator={availableContactDataLists.length > 3}>
                    {availableContactDataLists.map((list, index) => (
                      <View key={list._id} style={[styles.profileCardRow, index === 0 && styles.profileCardRowFirst, { flexDirection: rtl.rowDirection }]}>
                        <View style={[styles.profileCardIcon, { backgroundColor: '#10B9811F' }]}>
                          <Ionicons name="list-outline" size={15} color="#10B981" />
                        </View>
                        <View style={styles.attachmentInfo}>
                          <Text style={[styles.profileCardRowTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{list.name}</Text>
                          {!!list.description && (
                            <Text style={[styles.profileCardRowSubtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{list.description}</Text>
                          )}
                        </View>
                        {canManageContacts && (
                          <TouchableOpacity
                            style={styles.profileCardIconButton}
                            onPress={() => handleDataListMembershipChange(list._id, 'add')}
                            disabled={updatingDataListId === list._id}
                          >
                            {updatingDataListId === list._id ? (
                              <ActivityIndicator size="small" color={colors.primary} />
                            ) : (
                              <Ionicons name="add-outline" size={17} color={colors.primary} />
                            )}
                          </TouchableOpacity>
                        )}
                      </View>
                    ))}
                  </ScrollView>
                ) : (
                  !loadingContactDataLists && <Text style={styles.profileCardEmpty}>{t('contactEdit.cards.listsNoOthers')}</Text>
                )}
              </View>
            </View>

            {/* Orders — on mount only, capped to the 5 most recent; tapping a
                row goes to the existing order detail screen, the header "+"
                starts a new order prefilled with this contact. */}
            <View style={[styles.profileCard, compact && styles.profileCardInline, phoneCardWidth ? { width: phoneCardWidth } : null]}>
              <View style={[styles.profileCardHeader, { flexDirection: rtl.rowDirection }]}>
                <Text style={[styles.profileCardTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{t('contactEdit.section.orders')}</Text>
                {loadingOrders ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : canManageContacts && (
                  <TouchableOpacity onPress={handleAddOrderForContact} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                    <Ionicons name="add-circle-outline" size={18} color={colors.primary} />
                  </TouchableOpacity>
                )}
              </View>
              {orders.length > 0 ? (
                orders.slice(0, 3).map((order, index) => {
                  const orderColor = getOrderStatusColor(order.orderStatus);
                  const paymentColor = getPaymentStatusColor(order.paymentStatus);
                  return (
                    <View key={order._id} style={[styles.profileCardItem, index === 0 && styles.profileCardRowFirst]}>
                      <View style={[styles.profileCardItemTop, { flexDirection: rtl.rowDirection }]}>
                        <View style={[styles.profileCardIcon, { backgroundColor: `${orderColor}18` }]}>
                          <Ionicons name="receipt-outline" size={15} color={orderColor} />
                        </View>
                        <TouchableOpacity style={styles.attachmentInfo} onPress={() => navigateTo('order-edit', order._id)} activeOpacity={0.75}>
                          <View style={[styles.profileCardNumberRow, { flexDirection: rtl.rowDirection }]}>
                            <Text style={[styles.profileCardRowTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{order.orderNumber || t('contactEdit.cards.order')}</Text>
                            {!!order.orderNumber && (
                              <TouchableOpacity
                                style={styles.profileCardCopyButton}
                                onPress={(event) => {
                                  event.stopPropagation?.();
                                  copyCardNumber(order.orderNumber!, t('contactEdit.cards.orderNumber'));
                                }}
                                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                              >
                                <Ionicons name="copy-outline" size={13} color={colors.primary} />
                              </TouchableOpacity>
                            )}
                          </View>
                          <Text style={[styles.profileCardRowSubtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
                            {Array.isArray(order.items) ? t('contactEdit.cards.itemsCount', { count: order.items.length }) : t('contactEdit.cards.orderDetails')}
                          </Text>
                        </TouchableOpacity>
                        {typeof order.totalPrice === 'number' && (
                          <Text style={styles.profileCardRowTrailing} numberOfLines={1}>
                            {getCurrencySymbol(order.currency || businessCurrency())} {order.totalPrice.toFixed(0)}
                          </Text>
                        )}
                      </View>
                      <View style={[styles.profileCardActions, { flexDirection: rtl.rowDirection }]}>
                        <TouchableOpacity
                          style={[styles.statusChip, { backgroundColor: `${orderColor}18` }]}
                          onPress={() => setCardActionSheet({ kind: 'order-status', id: order._id, title: order.orderNumber || t('contactEdit.cards.orderStatus'), current: order.orderStatus })}
                        >
                          <Text style={[styles.statusChipText, { color: orderColor }]}>{statusLabel('order', order.orderStatus || 'pending')}</Text>
                          <Ionicons name="chevron-down" size={12} color={orderColor} />
                        </TouchableOpacity>
                        <TouchableOpacity
                          style={[styles.statusChip, { backgroundColor: `${paymentColor}18` }]}
                          onPress={() => setCardActionSheet({ kind: 'payment-status', id: order._id, title: order.orderNumber || t('contactEdit.cards.paymentStatus'), current: order.paymentStatus || 'unpaid' })}
                        >
                          <Ionicons name="card-outline" size={13} color={paymentColor} />
                          <Text style={[styles.statusChipText, { color: paymentColor }]}>{statusLabel('payment', order.paymentStatus || 'unpaid')}</Text>
                          <Ionicons name="chevron-down" size={12} color={paymentColor} />
                        </TouchableOpacity>
                        <TouchableOpacity style={styles.profileCardIconButton} onPress={() => navigateTo('order-edit', order._id)}>
                          <Ionicons name="open-outline" size={15} color={colors.text.secondary} />
                        </TouchableOpacity>
                      </View>
                    </View>
                  );
                })
              ) : (
                !loadingOrders && <Text style={styles.profileCardEmpty}>{t('contactEdit.orders.empty')}</Text>
              )}
            </View>

            {/* Quotations — same pattern as Orders above. */}
            <View style={[styles.profileCard, compact && styles.profileCardInline, phoneCardWidth ? { width: phoneCardWidth } : null]}>
              <View style={[styles.profileCardHeader, { flexDirection: rtl.rowDirection }]}>
                <Text style={[styles.profileCardTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{t('contactEdit.section.quotations')}</Text>
                {loadingQuotations ? (
                  <ActivityIndicator size="small" color={colors.primary} />
                ) : canManageContacts && (
                  <TouchableOpacity onPress={handleAddQuotationForContact} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                    <Ionicons name="add-circle-outline" size={18} color={colors.primary} />
                  </TouchableOpacity>
                )}
              </View>
              {quotations.length > 0 ? (
                quotations.slice(0, 3).map((quotation, index) => {
                  const statusColor = getQuotationStatusColor(quotation.quotationStatus);
                  return (
                    <View key={quotation._id} style={[styles.profileCardItem, index === 0 && styles.profileCardRowFirst]}>
                      <View style={[styles.profileCardItemTop, { flexDirection: rtl.rowDirection }]}>
                        <View style={[styles.profileCardIcon, { backgroundColor: `${statusColor}18` }]}>
                          <Ionicons name="document-text-outline" size={15} color={statusColor} />
                        </View>
                        <TouchableOpacity style={styles.attachmentInfo} onPress={() => navigateTo('quotation-detail', quotation._id)} activeOpacity={0.75}>
                          <View style={[styles.profileCardNumberRow, { flexDirection: rtl.rowDirection }]}>
                            <Text style={[styles.profileCardRowTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{quotation.quotationNumber || t('contactEdit.cards.quotation')}</Text>
                            {!!quotation.quotationNumber && (
                              <TouchableOpacity
                                style={styles.profileCardCopyButton}
                                onPress={(event) => {
                                  event.stopPropagation?.();
                                  copyCardNumber(quotation.quotationNumber!, t('contactEdit.cards.quotationNumber'));
                                }}
                                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                              >
                                <Ionicons name="copy-outline" size={13} color={colors.primary} />
                              </TouchableOpacity>
                            )}
                          </View>
                          <Text style={[styles.profileCardRowSubtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
                            {Array.isArray(quotation.items) ? t('contactEdit.cards.itemsCount', { count: quotation.items.length }) : t('contactEdit.cards.quotationDetails')}
                          </Text>
                        </TouchableOpacity>
                        {typeof quotation.totalPrice === 'number' && (
                          <Text style={styles.profileCardRowTrailing} numberOfLines={1}>
                            {getCurrencySymbol(quotation.currency || businessCurrency())} {quotation.totalPrice.toFixed(0)}
                          </Text>
                        )}
                      </View>
                      <View style={[styles.profileCardActions, { flexDirection: rtl.rowDirection }]}>
                        <TouchableOpacity
                          style={[styles.statusChip, { backgroundColor: `${statusColor}18` }]}
                          onPress={() => setCardActionSheet({ kind: 'quotation-status', id: quotation._id, title: quotation.quotationNumber || t('contactEdit.cards.quotationStatus'), current: quotation.quotationStatus || 'draft' })}
                        >
                          <Text style={[styles.statusChipText, { color: statusColor }]}>{statusLabel('quotation', quotation.quotationStatus || 'draft')}</Text>
                          <Ionicons name="chevron-down" size={12} color={statusColor} />
                        </TouchableOpacity>
                        <TouchableOpacity style={styles.profileCardActionButton} onPress={() => navigateTo('quotation-edit', quotation._id)}>
                          <Ionicons name="create-outline" size={14} color={colors.primary} />
                          <Text style={styles.profileCardActionButtonText}>{t('contactEdit.cards.edit')}</Text>
                        </TouchableOpacity>
                        <TouchableOpacity style={styles.profileCardIconButton} onPress={() => navigateTo('quotation-detail', quotation._id)}>
                          <Ionicons name="open-outline" size={15} color={colors.text.secondary} />
                        </TouchableOpacity>
                      </View>
                    </View>
                  );
                })
              ) : (
                !loadingQuotations && <Text style={styles.profileCardEmpty}>{t('contactEdit.quotations.empty')}</Text>
              )}
            </View>
            </ProfileCardsStrip>

            {/* Visits — history for this contact with directions, start/end
                (via VisitExecuteScreen, which owns location/photo enforcement
                and outcome capture), claim and cancel. */}
            <ContactVisitsCard
              contactId={contact.id}
              contactName={name.trim() || contact.name || ''}
              location={location}
              compact={compact}
              refreshKey={`${profileRefreshKey ?? ''}:${visitsRefresh}`}
              activeVisitId={activeVisitId}
              onOpenVisit={(visitId) => setActiveVisitId(visitId)}
            />

            </View>
          </View>
        )}

        {/* Manage Channels (Identifiers) + Notes — side by side in compact
            (pane) mode, where Identifiers is always visible (no pencil-toggle
            needed — there's a dedicated column for it now) instead of only
            appearing when showChannelsEditor is toggled on. Non-compact native/
            mobile keeps the original pencil-toggled, full-width behavior for
            Identifiers and a separate standalone Notes section below (see
            notesSectionContent's non-compact render further down). */}
        {(compact || showChannelsEditor) && (
          <View style={[styles.formSection, compact && styles.cardsRow, isPhonePane && styles.cardsStacked]}>
          <View style={compact ? [styles.profileCardInRow, isPhonePane && styles.profileCardStacked] : undefined}>
            <View style={[styles.sectionHeader, { flexDirection: rtl.rowDirection }]}>
              <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contactEdit.section.identifiers')}</Text>
              <View style={[styles.addButtonContainer, showIdentifierMenu && styles.addButtonContainerOpen]}>
                <TouchableOpacity
                  style={styles.addButton}
                  onPress={openIdentifierMenu}
                  {...(Platform.OS === 'web' && {
                    onClick: (e: any) => {
                      e.stopPropagation();
                      e.nativeEvent?.stopPropagation?.();
                    },
                  })}
                >
                  <Ionicons name="add-circle" size={24} color={colors.primary} />
                </TouchableOpacity>
              </View>
            </View>

            <ScrollView
              style={compact ? styles.identifiersScrollArea : undefined}
              nestedScrollEnabled
              showsVerticalScrollIndicator={compact && channelIdentifiers.length > 3}
            >
            {channelIdentifiers.map((identifier, index) => {
              const sameTypeCount = channelIdentifiers.filter(id => id.type === identifier.type).length;
              const hasRealValue = !!identifier.value.trim() && !identifier.value.includes('*');
              // Quick-add offers whichever OTHER phone-based channel doesn't
              // already carry THIS SAME number — offered both when the
              // sibling type is entirely absent (adds a new row) and when it
              // exists with a DIFFERENT value (replaces it), so typing a new
              // number into one type always offers to sync it into the
              // other rather than leaving a stale mismatched value behind.
              // Not offered once the sibling already matches (already in
              // sync). Once used, the button flips to "Undo" (tracked via
              // quickAddedKey) instead of disappearing, per product decision.
              // Deliberately NOT gated on isFreshlyAdded — this must show for
              // an ALREADY-SAVED identifier too (editing an existing contact
              // that only has one of phone/whatsapp), not just one added in
              // the current editing session, before AND after save.
              const siblingType = PHONE_BASED_CHANNELS.find(channelType => channelType !== identifier.type);
              const normalizedValue = identifier.value.replace(/\D/g, '');
              const matchingSibling = siblingType
                ? channelIdentifiers.find(id => id.type === siblingType && id.key !== identifier.quickAddedKey)
                : undefined;
              const siblingHasDifferentRealValue =
                !!matchingSibling &&
                !!matchingSibling.value.trim() &&
                !matchingSibling.value.includes('*') &&
                matchingSibling.value.replace(/\D/g, '') !== normalizedValue;
              const siblingAlreadyInSync =
                !!matchingSibling && matchingSibling.value.replace(/\D/g, '') === normalizedValue && !!normalizedValue;
              const showQuickAddOffer =
                !identifier.quickAddedKey &&
                hasRealValue &&
                PHONE_BASED_CHANNELS.includes(identifier.type) &&
                !!siblingType &&
                !siblingAlreadyInSync;
              const showQuickAddUndo = !!identifier.quickAddedKey;

              return (
                <View key={identifier.key} style={styles.identifierRow}>
                <View style={[styles.identifierRowHeader, { flexDirection: rtl.rowDirection }]}>
                  <View style={[styles.identifierTypeBadge, { flexDirection: rtl.rowDirection }]}>
                    <Text style={[styles.identifierTypeText, { color: getChannelColor(identifier.type) }]}>
                      {getChannelName(identifier.type, t)}
                    </Text>
                    {identifier.isPrimary && (
                      <View style={styles.identifierPrimaryBadge}>
                        <Text style={styles.identifierPrimaryBadgeText}>{t('contacts.primary')}</Text>
                      </View>
                    )}
                  </View>
                  <View style={[styles.identifierRowActions, { flexDirection: rtl.rowDirection }]}>
                    {false && sameTypeCount > 1 && !identifier.isPrimary && hasRealValue && contact?.id && !identifier.isFreshlyAdded && (
                      <TouchableOpacity
                        style={styles.setPrimaryButton}
                        onPress={() => setPrimaryIdentifier(index)}
                      >
                        <Ionicons name="star-outline" size={14} color={colors.primary} />
                        <Text style={styles.setPrimaryButtonText}>{t('contactEdit.identifier.setPrimary')}</Text>
                      </TouchableOpacity>
                    )}
                    <TouchableOpacity
                      style={styles.removeIdentifierButton}
                      onPress={() => removeChannelIdentifier(index)}
                    >
                      <Ionicons name="close-circle" size={20} color={colors.error} />
                    </TouchableOpacity>
                  </View>
                </View>
                {identifier.type === 'phone' || identifier.type === 'whatsapp' ? (
                  <PhoneInputComponent
                    ref={(ref) => {
                      if (ref) phoneInputRefs.current[`${identifier.type}_${index}`] = ref;
                    }}
                    value={identifier.value}
                    onChangeText={(value) => updateChannelIdentifier(index, value)}
                    placeholder={t('contactEdit.identifier.enterNumber', { channel: getChannelName(identifier.type, t) })}
                    style={styles.identifierInput}
                    initialCountry={identifier.countryCode || defaultPhoneCountry()}
                  />
                ) : (
                  <Input
                    value={identifier.value}
                    onChangeText={(value) => updateChannelIdentifier(index, value)}
                    placeholder={t('contactEdit.identifier.enter', { channel: getChannelName(identifier.type, t) })}
                    keyboardType={identifier.type === 'email' ? 'email-address' : 'default'}
                    autoCapitalize="none"
                    style={styles.identifierInput}
                  />
                )}
                {showQuickAddOffer && siblingType && (
                  <TouchableOpacity
                    style={styles.quickAddSiblingButton}
                    onPress={() => addSiblingPhoneIdentifier(index, siblingType)}
                  >
                    <Ionicons name="add-circle-outline" size={14} color={colors.primary} />
                    <Text style={styles.quickAddSiblingButtonText}>
                      {t(
                        siblingHasDifferentRealValue
                          ? 'contactEdit.identifier.useForChannel'
                          : 'contactEdit.identifier.addForChannel',
                        { channel: getChannelName(siblingType, t) }
                      )}
                    </Text>
                  </TouchableOpacity>
                )}
                {showQuickAddUndo && (
                  <TouchableOpacity
                    style={styles.quickAddSiblingButton}
                    onPress={() => undoSiblingPhoneIdentifier(index)}
                  >
                    <Ionicons name="arrow-undo-outline" size={14} color={colors.error} />
                    <Text style={[styles.quickAddSiblingButtonText, { color: colors.error }]}>
                      {t('contactEdit.identifier.undoAdd')}
                    </Text>
                  </TouchableOpacity>
                )}
                </View>
              );
            })}

              {channelIdentifiers.length === 0 && (
                <Text style={styles.emptyText}>{t('contactEdit.identifier.emptyHint')}</Text>
              )}
            </ScrollView>
          </View>

          {compact && !isNewContact && (
            <View style={[styles.profileCardInRow, isPhonePane && styles.profileCardStacked]}>
              <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contactEdit.section.notes')}</Text>
              <NotesSection
                notes={notes}
                onAddNote={handleAddNote}
                onDeleteNote={handleDeleteNote}
                currentUserId={user?.id}
                showHeader={false}
                editable={canManageNotes}
                compact
                previewCount={3}
                maxListHeight={220}
              />
              {loadingNotes && notes.length === 0 && (
                <Text style={styles.emptyText}>{t('contactEdit.notes.loading')}</Text>
              )}
            </View>
          )}
          </View>
        )}

        {/* Notes — non-compact (native/mobile) only; compact mode renders
            this alongside Identifiers above instead. */}
        {!compact && !isNewContact && (
          <View style={styles.formSection}>
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contactEdit.section.notes')}</Text>
            <NotesSection
              notes={notes}
              onAddNote={handleAddNote}
              onDeleteNote={handleDeleteNote}
              currentUserId={user?.id}
              showHeader={false}
              editable={canManageNotes}
            />
            {loadingNotes && notes.length === 0 && (
              <Text style={styles.emptyText}>{t('contactEdit.notes.loading')}</Text>
            )}
          </View>
        )}

        {/* Workflow runs — this contact's runs of every flow, with stop / resume / continue from a step. */}
        {!isNewContact && !!contact?.id && (
          <View style={styles.formSection}>
            <ContactFlowRuns contactId={(contact as any)?.parentContact?._id || contact.id} />
          </View>
        )}

        {/* Location — compact (pane) mode splits into a real map preview
            (pinned to the saved coordinates, height matched to the fields
            column beside it) and the detail fields — including the maps-link
            paste shortcut, grouped in with the rest of the address fields
            instead of floating below the whole row; non-compact keeps the
            original tap-to-open button + tight 2-col field grid, which
            already fits a narrow mobile screen well. */}
        <View style={styles.formSection}>
          <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contactEdit.section.location')}</Text>
          <View style={compact ? [styles.locationCompactRow, { flexDirection: isPhonePane ? 'column' : rtl.rowDirection }, isPhonePane && styles.locationStacked] : undefined}>
            {compact ? (
              <TouchableOpacity
                style={[styles.locationThumb, isPhonePane ? styles.locationThumbStacked : locationFieldsHeight ? { height: locationFieldsHeight } : null]}
                onPress={() => setShowLocationPicker(true)}
              >
                {locationMapEmbedUrl && Platform.OS === 'web' ? (
                  <>
                    {React.createElement('iframe' as any, {
                      src: locationMapEmbedUrl,
                      style: {
                        width: '100%',
                        height: '100%',
                        border: 0,
                        display: 'block',
                        pointerEvents: 'none',
                      },
                      loading: 'lazy',
                    })}
                    <View pointerEvents="none" style={styles.locationThumbPin}>
                      <Ionicons name="location" size={28} color={colors.primary} />
                    </View>
                  </>
                ) : locationMapImageUrl ? (
                  <>
                    <Image source={{ uri: locationMapImageUrl }} style={styles.locationThumbImage as any} resizeMode="cover" />
                    <View pointerEvents="none" style={styles.locationThumbPin}>
                      <Ionicons name="location" size={28} color={colors.primary} />
                    </View>
                  </>
                ) : (
                  <View style={styles.locationThumbPlaceholder}>
                    <Ionicons name="location-outline" size={28} color={colors.text.secondary} />
                    <Text style={styles.locationThumbText}>{t('contactEdit.location.select')}</Text>
                  </View>
                )}
              </TouchableOpacity>
            ) : (
              <TouchableOpacity
                style={[styles.locationButton, { flexDirection: rtl.rowDirection }]}
                onPress={() => setShowLocationPicker(true)}
              >
                <Ionicons name="location" size={20} color={colors.primary} />
                <Text style={[styles.locationButtonText, { textAlign: rtl.textStart }]}>
                  {location?.address || t('contactEdit.location.select')}
                </Text>
                <Ionicons name={rtl.mirrorIcon('chevron-forward') as any} size={20} color={colors.text.secondary} />
              </TouchableOpacity>
            )}

            <View
              style={compact ? styles.locationFieldsColumn : undefined}
              onLayout={compact ? (e) => setLocationFieldsHeight(Math.round(e.nativeEvent.layout.height)) : undefined}
            >
              {compact ? (
                <>
                  <Input
                    value={location?.address || ''}
                    onChangeText={(value) => setLocation(prev => ({ ...prev, address: value }))}
                    placeholder={t('contactEdit.location.addressLine')}
                    containerStyle={styles.locationNationalityInput}
                  />
                  <Input
                    value={location?.city || ''}
                    onChangeText={(value) => setLocation(prev => ({ ...prev, city: value }))}
                    placeholder={t('contactEdit.location.city')}
                    containerStyle={styles.locationNationalityInput}
                  />
                  <View style={styles.locationNationalityInput}>
                    <CountryPicker
                      label={t('contactEdit.location.country')}
                      value={location?.country || ''}
                      onChange={(value) => setLocation(prev => ({ ...prev, country: value }))}
                    />
                  </View>
                  <View style={styles.locationNationalityInput}>
                    <CountryPicker
                      label={t('contactEdit.location.nationality')}
                      value={nationality}
                      onChange={setNationality}
                    />
                  </View>

                  <TouchableOpacity
                    style={styles.mapsUrlToggle}
                    onPress={() => setShowMapsUrlInput((v) => !v)}
                  >
                    <Text style={styles.mapsUrlToggleText}>
                      {showMapsUrlInput ? t('contactEdit.location.hideLinkPaste') : t('contactEdit.location.showLinkPaste')}
                    </Text>
                  </TouchableOpacity>
                  {showMapsUrlInput && (
                    <View style={[styles.mapsUrlRow, { flexDirection: rtl.rowDirection }]}>
                      <Input
                        value={mapsUrlInput}
                        onChangeText={setMapsUrlInput}
                        placeholder={t('contactEdit.location.pasteLinkPlaceholder')}
                        autoCapitalize="none"
                        latin
                        containerStyle={styles.mapsUrlInput}
                        editable={!resolvingMapsUrl}
                      />
                      <TouchableOpacity
                        style={[styles.mapsUrlButton, (!mapsUrlInput.trim() || resolvingMapsUrl) && styles.mapsUrlButtonDisabled]}
                        onPress={handleResolveMapsUrl}
                        disabled={!mapsUrlInput.trim() || resolvingMapsUrl}
                      >
                        {resolvingMapsUrl ? (
                          <ActivityIndicator size="small" color={colors.white} />
                        ) : (
                          <DirectionalIcon name="arrow-forward" size={18} color={colors.white} />
                        )}
                      </TouchableOpacity>
                    </View>
                  )}
                </>
              ) : (
                <>
                  <View style={[styles.locationDetailRow, { flexDirection: rtl.rowDirection }]}>
                    <Input
                      value={location?.address || ''}
                      onChangeText={(value) => setLocation(prev => ({ ...prev, address: value }))}
                      placeholder={t('contactEdit.location.addressLine')}
                      containerStyle={styles.locationDetailInput}
                    />
                    <Input
                      value={location?.city || ''}
                      onChangeText={(value) => setLocation(prev => ({ ...prev, city: value }))}
                      placeholder={t('contactEdit.location.city')}
                      containerStyle={styles.locationDetailInput}
                    />
                  </View>
                  <View style={[styles.locationDetailRow, { flexDirection: rtl.rowDirection }]}>
                    <View style={styles.locationDetailInput}>
                      <CountryPicker
                        label={t('contactEdit.location.country')}
                        value={location?.country || ''}
                        onChange={(value) => setLocation(prev => ({ ...prev, country: value }))}
                      />
                    </View>
                    <View style={styles.locationDetailInput}>
                      <CountryPicker
                        label={t('contactEdit.location.nationality')}
                        value={nationality}
                        onChange={setNationality}
                      />
                    </View>
                  </View>
                </>
              )}
            </View>
          </View>

          {/* Non-compact only: compact mode groups this with the fields
              column above instead. */}
          {!compact && (
            <>
              <TouchableOpacity
                style={styles.mapsUrlToggle}
                onPress={() => setShowMapsUrlInput((v) => !v)}
              >
                <Text style={styles.mapsUrlToggleText}>
                  {showMapsUrlInput ? t('contactEdit.location.hideLinkPaste') : t('contactEdit.location.showLinkPaste')}
                </Text>
              </TouchableOpacity>
              {showMapsUrlInput && (
                <View style={[styles.mapsUrlRow, { flexDirection: rtl.rowDirection }]}>
                  <Input
                    value={mapsUrlInput}
                    onChangeText={setMapsUrlInput}
                    placeholder={t('contactEdit.location.pasteLinkPlaceholder')}
                    autoCapitalize="none"
                    latin
                    containerStyle={styles.mapsUrlInput}
                    editable={!resolvingMapsUrl}
                  />
                  <TouchableOpacity
                    style={[styles.mapsUrlButton, (!mapsUrlInput.trim() || resolvingMapsUrl) && styles.mapsUrlButtonDisabled]}
                    onPress={handleResolveMapsUrl}
                    disabled={!mapsUrlInput.trim() || resolvingMapsUrl}
                  >
                    {resolvingMapsUrl ? (
                      <ActivityIndicator size="small" color={colors.white} />
                    ) : (
                      <DirectionalIcon name="arrow-forward" size={18} color={colors.white} />
                    )}
                  </TouchableOpacity>
                </View>
              )}
            </>
          )}
        </View>

        {/* Documents / Tags — inline in compact pane mode, stacked in full
            edit mode. */}
        <View style={[styles.formSection, compact && styles.cardsRow]}>
        {compact && (
          <View style={styles.profileCardInRow}>
            <View style={styles.profileCard}>
              <View style={[styles.profileCardHeader, { flexDirection: rtl.rowDirection }]}>
                <Text style={[styles.profileCardTitle, { textAlign: rtl.textStart }]}>{t('contactEdit.section.documents')}</Text>
                {canManageContacts && (
                  <TouchableOpacity onPress={handleDocumentUpload} disabled={uploadingAttachment}>
                    {uploadingAttachment ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <Ionicons name="document-attach" size={22} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                )}
              </View>
              {attachments.length > 0 ? (
                attachments.map((attachment, index) => (
                  <View
                    key={attachment.url}
                    style={[styles.profileCardRow, index === 0 && styles.profileCardRowFirst, { flexDirection: rtl.rowDirection }]}
                  >
                    <Ionicons name="document" size={18} color={colors.primary} />
                    <View style={[styles.attachmentInfo, rtl.marginStart(spacing.sm)]}>
                      <Text style={[styles.profileCardRowTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{attachment.name}</Text>
                      <Text style={[styles.profileCardRowSubtitle, { textAlign: rtl.textStart }]}>
                        {t('contactEdit.documents.sizeKb', { size: (attachment.size / 1024).toFixed(2) })}
                      </Text>
                    </View>
                    {canManageContacts && (
                      <TouchableOpacity onPress={() => handleRemoveAttachment(attachment)} disabled={removingAttachmentUrl === attachment.url}>
                        {removingAttachmentUrl === attachment.url ? (
                          <ActivityIndicator size="small" color={colors.error} />
                        ) : (
                          <Ionicons name="trash-outline" size={18} color={colors.error} />
                        )}
                      </TouchableOpacity>
                    )}
                  </View>
                ))
              ) : (
                <Text style={styles.profileCardEmpty}>{t('contactEdit.documents.empty')}</Text>
              )}
            </View>
          </View>
        )}

        <View style={compact ? styles.profileCardInRow : undefined}>
          <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contactEdit.section.tags')}</Text>

          {/* Search only shown once there are enough tags that finding one
              to remove is actually hard — keeps the section lean for
              contacts with just a couple of tags. */}
          {tags.length > 6 && (
            <View style={[styles.tagSearchContainer, { flexDirection: rtl.rowDirection }]}>
              <Ionicons name="search" size={16} color={colors.text.secondary} />
              <TextInput
                style={[styles.tagSearchInput, { textAlign: rtl.textStart }]}
                value={tagSearch}
                onChangeText={setTagSearch}
                placeholder={t('contactEdit.tags.searchPlaceholder')}
                placeholderTextColor={colors.text.secondary as string}
              />
              {tagSearch.length > 0 && (
                <TouchableOpacity onPress={() => setTagSearch('')} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                  <Ionicons name="close-circle" size={16} color={colors.text.secondary} />
                </TouchableOpacity>
              )}
            </View>
          )}

          {/* Height-capped so a large tag count scrolls internally instead
              of pushing the rest of the form down / overflowing the pane. */}
          <ScrollView
            style={styles.tagsScrollArea}
            contentContainerStyle={[styles.tagsContainer, { flexDirection: rtl.rowDirection }]}
            nestedScrollEnabled
            showsVerticalScrollIndicator={tags.length > 6}
          >
            {tags
              .filter((tag) => tag.toLowerCase().includes(tagSearch.trim().toLowerCase()))
              .map((tag) => (
                <View key={tag} style={[styles.tag, { flexDirection: rtl.rowDirection }]}>
                  <Text style={styles.tagText} numberOfLines={1}>{tag}</Text>
                  {canManageTags && (
                    <TouchableOpacity onPress={() => handleRemoveTag(tag)} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
                      <Ionicons name="close-circle" size={16} color={colors.text.secondary} />
                    </TouchableOpacity>
                  )}
                </View>
              ))}
            {tags.length > 0 && tagSearch.trim() && !tags.some((tag) => tag.toLowerCase().includes(tagSearch.trim().toLowerCase())) && (
              <Text style={styles.emptyText}>{t('contactEdit.tags.noMatch')}</Text>
            )}
          </ScrollView>

          {canManageTags && (
            <View style={[styles.addTagContainer, { flexDirection: rtl.rowDirection }]}>
              <Input
                value={newTag}
                onChangeText={setNewTag}
                placeholder={t('contactEdit.tags.addPlaceholder')}
                onSubmitEditing={handleAddTag}
                returnKeyType="done"
              />
              <TouchableOpacity
                style={styles.addTagButton}
                onPress={handleAddTag}
                disabled={!newTag.trim()}
              >
                <Ionicons
                  name="add"
                  size={20}
                  color={newTag.trim() ? colors.primary : colors.text.disabled}
                />
              </TouchableOpacity>
            </View>
          )}
        </View>
        </View>

        {/* Documents — non-compact (native/mobile) only. */}
        {!compact && (
          <View style={styles.formSection}>
            <View style={styles.profileCard}>
              <View style={[styles.profileCardHeader, { flexDirection: rtl.rowDirection }]}>
                <Text style={[styles.profileCardTitle, { textAlign: rtl.textStart }]}>{t('contactEdit.section.documents')}</Text>
                {canManageContacts && (
                  <TouchableOpacity onPress={handleDocumentUpload} disabled={uploadingAttachment}>
                    {uploadingAttachment ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <Ionicons name="document-attach" size={22} color={colors.primary} />
                    )}
                  </TouchableOpacity>
                )}
              </View>
              {attachments.length > 0 ? (
                attachments.map((attachment, index) => (
                  <View
                    key={attachment.url}
                    style={[styles.profileCardRow, index === 0 && styles.profileCardRowFirst, { flexDirection: rtl.rowDirection }]}
                  >
                    <Ionicons name="document" size={18} color={colors.primary} />
                    <View style={[styles.attachmentInfo, rtl.marginStart(spacing.sm)]}>
                      <Text style={[styles.profileCardRowTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{attachment.name}</Text>
                      <Text style={[styles.profileCardRowSubtitle, { textAlign: rtl.textStart }]}>
                        {t('contactEdit.documents.sizeKb', { size: (attachment.size / 1024).toFixed(2) })}
                      </Text>
                    </View>
                    {canManageContacts && (
                      <TouchableOpacity onPress={() => handleRemoveAttachment(attachment)} disabled={removingAttachmentUrl === attachment.url}>
                        {removingAttachmentUrl === attachment.url ? (
                          <ActivityIndicator size="small" color={colors.error} />
                        ) : (
                          <Ionicons name="trash-outline" size={18} color={colors.error} />
                        )}
                      </TouchableOpacity>
                    )}
                  </View>
                ))
              ) : (
                <Text style={styles.profileCardEmpty}>{t('contactEdit.documents.empty')}</Text>
              )}
            </View>
          </View>
        )}

        {/* Extra Fields (item 2) — custom columns on the linked system
            Contacts Data List, alongside this contact's own fixed fields
            above. Existing contacts only, same reasoning as Notes above. */}
        {!isNewContact && listId && customColumns.length > 0 && currentBusiness?._id && contact?.id && (
          <View style={styles.formSection}>
            <Text style={[styles.sectionTitle, { textAlign: rtl.textStart }]}>{t('contactEdit.cards.extraFields')}</Text>
            {customColumns.map((col) => (
              <DataListFieldRenderer
                key={col.id}
                column={col}
                value={customValues[col.id]}
                onChange={(v) => setCustomValues((prev) => ({ ...prev, [col.id]: v }))}
                error={customValueErrors[col.id]}
                businessId={currentBusiness._id}
                listId={listId}
                recordId={contact.id}
              />
            ))}
            <Button
              title="Save Extra Fields"
              onPress={handleSaveCustomValues}
              loading={savingCustomValues}
              variant="secondary"
            />
          </View>
        )}

      </ScrollView>

      {/* Identifier Menu - Rendered outside ScrollView for proper z-index on web */}

      {/* Location Picker Modal */}
      <MapLocationPicker
        visible={showLocationPicker}
        onClose={() => setShowLocationPicker(false)}
        onLocationSelect={(loc) => {
          setLocation(loc);
          setShowLocationPicker(false);
        }}
        initialLocation={location || null}
      />

      {!isNewContact && contact?.id && (
        <UserAssignmentModal
          visible={showAssignmentModal}
          onClose={() => setShowAssignmentModal(false)}
          contactId={contact.id}
          contactName={name}
          onAssignmentChange={() => {
            setShowAssignmentModal(false);
            loadNotes(contact.id);
            notifyProfileCardMutation();
          }}
        />
      )}

      {/* Delete confirmation — replaces Alert.alert, which renders nothing
          on web, so delete previously had no real confirmation there. */}
      <ConfirmationModal
        visible={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={confirmDelete}
        title={t('contactEdit.delete.title')}
        message={contact ? t('contactEdit.delete.confirmMessage', { name: contact.name }) : ''}
        confirmText={t('contactEdit.delete.confirm')}
        cancelText={t('contactEdit.delete.cancel')}
        type="error"
        loading={isDeleting}
      />

      {/* Add-identifier menu — see openIdentifierMenu. */}
      <Modal visible={showIdentifierMenu} transparent animationType="fade" onRequestClose={closeIdentifierMenu}>
        <TouchableOpacity style={styles.identifierMenuBackdrop} activeOpacity={1} onPress={closeIdentifierMenu}>
          <View
            ref={identifierMenuRef}
            style={[
              styles.identifierMenu,
              styles.identifierMenuFloating,
              (() => {
                const MENU_WIDTH = 220;
                const EDGE = 8;
                if (!identifierMenuAnchor) return { top: 120, left: Math.max(EDGE, (windowWidth - MENU_WIDTH) / 2), width: MENU_WIDTH };
                const preferred = rtl.isRTL ? identifierMenuAnchor.x : identifierMenuAnchor.x + identifierMenuAnchor.width - MENU_WIDTH;
                return {
                  top: identifierMenuAnchor.y + identifierMenuAnchor.height + 4,
                  left: Math.min(Math.max(EDGE, preferred), windowWidth - MENU_WIDTH - EDGE),
                  width: MENU_WIDTH,
                };
              })(),
            ]}
          >
            {IDENTIFIER_MENU_ITEMS.map((item) => (
              <TouchableOpacity
                key={item.type}
                style={[styles.menuItem, { flexDirection: rtl.rowDirection }]}
                onPress={() => {
                  closeIdentifierMenu();
                  addChannelIdentifier(item.type);
                }}
              >
                <Ionicons name={item.icon} size={18} color={item.color} />
                <Text style={[styles.menuItemText, { textAlign: rtl.textStart }]}>{t(item.labelKey as any)}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </TouchableOpacity>
      </Modal>

      <Modal
        transparent
        visible={!!cardActionSheet}
        animationType="fade"
        onRequestClose={() => setCardActionSheet(null)}
      >
        <TouchableOpacity style={styles.actionSheetBackdrop} activeOpacity={1} onPress={() => setCardActionSheet(null)}>
          <TouchableOpacity activeOpacity={1} style={styles.actionSheetCard}>
            <View style={[styles.actionSheetHeader, { flexDirection: rtl.rowDirection }]}>
              <Text style={[styles.actionSheetTitle, { textAlign: rtl.textStart }]} numberOfLines={1}>
                {cardActionSheet?.title || ''}
              </Text>
              <TouchableOpacity onPress={() => setCardActionSheet(null)} style={styles.profileCardIconButton}>
                <Ionicons name="close" size={16} color={colors.text.secondary} />
              </TouchableOpacity>
            </View>
            {(cardActionSheet?.kind === 'order-status'
              ? ORDER_STATUS_OPTIONS
              : cardActionSheet?.kind === 'payment-status'
              ? PAYMENT_STATUS_OPTIONS
              : QUOTATION_STATUS_OPTIONS
            ).map((option) => {
              const active = cardActionSheet?.current === option.value;
              const color = cardActionSheet?.kind === 'order-status'
                ? getOrderStatusColor(option.value)
                : cardActionSheet?.kind === 'payment-status'
                ? getPaymentStatusColor(option.value)
                : getQuotationStatusColor(option.value);
              const loading = !!cardActionSheet && updatingCardAction === `${cardActionSheet.kind === 'quotation-status' ? 'quotationStatus' : cardActionSheet.kind === 'payment-status' ? 'paymentStatus' : 'orderStatus'}:${cardActionSheet.id}`;
              return (
                <TouchableOpacity
                  key={option.value}
                  style={[styles.actionSheetOption, active && { backgroundColor: `${color}14` }, { flexDirection: rtl.rowDirection }]}
                  disabled={loading}
                  onPress={() => {
                    if (!cardActionSheet) return;
                    if (cardActionSheet.kind === 'order-status') {
                      handleUpdateOrderField(cardActionSheet.id, 'orderStatus', option.value);
                    } else if (cardActionSheet.kind === 'payment-status') {
                      handleUpdateOrderField(cardActionSheet.id, 'paymentStatus', option.value);
                    } else {
                      handleUpdateQuotationStatus(cardActionSheet.id, option.value);
                    }
                  }}
                >
                  <View style={[styles.actionSheetDot, { backgroundColor: color }]} />
                  <Text style={[styles.actionSheetOptionText, { color: active ? color : colors.textDark }]}>{statusLabel(cardActionSheet?.kind === 'order-status' ? 'order' : cardActionSheet?.kind === 'payment-status' ? 'payment' : 'quotation', option.value)}</Text>
                  {active && <Ionicons name="checkmark" size={18} color={color} />}
                </TouchableOpacity>
              );
            })}
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>

      {/* Channel icon picker — opens when a type (e.g. WhatsApp) has more
          than one identifier, letting the user choose which one to open a
          conversation on, mirroring ContactsList.tsx's contact-card icon
          pickers (same tap-anchored popover positioning). */}
      <Modal
        transparent
        visible={!!channelIconPickerType}
        animationType="fade"
        onRequestClose={() => setChannelIconPickerType(null)}
      >
        <TouchableOpacity
          style={styles.iconPickerBackdrop}
          activeOpacity={1}
          onPress={() => setChannelIconPickerType(null)}
        >
          <View
            style={[
              styles.iconPickerContainer,
              channelIconPickerPosition
                ? {
                    top: Math.max(
                      spacing.sm,
                      Math.min(
                        channelIconPickerPosition.y + 8,
                        screenHeight - (36 + visibleChannelIdentifiers.filter(i => i.type === channelIconPickerType).length * 32)
                      )
                    ),
                    left: Math.max(spacing.sm, Math.min(channelIconPickerPosition.x - 100, screenWidth - 220)),
                  }
                : { top: 60, right: spacing.lg },
            ]}
          >
            <View style={styles.iconPickerCard}>
              <Text style={[styles.iconPickerTitle, { textAlign: rtl.textStart }]}>
                {channelIconPickerType ? getChannelName(channelIconPickerType, t) : ''}
              </Text>
              {visibleChannelIdentifiers
                .map((identifier, index) => ({ identifier, index }))
                .filter(({ identifier }) => identifier.type === channelIconPickerType && !!identifier.value.trim())
                .map(({ identifier, index }) => (
                  <TouchableOpacity
                    key={identifier.key}
                    style={[styles.iconPickerRow, { flexDirection: rtl.rowDirection }]}
                    onPress={() => {
                      setChannelIconPickerType(null);
                      handleChannelPress(identifier);
                    }}
                  >
                    <Ionicons name={getChannelIcon(identifier.type)} size={14} color={getChannelColor(identifier.type)} />
                    <Text style={styles.iconPickerRowText} numberOfLines={1}>{formatIdentifierForDisplay(identifier)}</Text>
                    {identifier.isPrimary && (
                      <Text style={styles.iconPickerPrimaryTag}>{t('contacts.primary')}</Text>
                    )}
                  </TouchableOpacity>
                ))}
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
};

// Helper functions
// Channels that open an existing conversation when tapped — restricted to
// what createOrFindConversation actually supports (mirrors
// PhoneCallScreen.tsx's identical OPENABLE_CHANNELS).
const OPENABLE_CHANNELS = new Set(['whatsapp', 'facebook-messenger']);

const ORDER_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'placed', label: 'Placed' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'processing', label: 'Processing' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const PAYMENT_STATUS_OPTIONS = [
  { value: 'unpaid', label: 'Unpaid' },
  { value: 'partial', label: 'Partial' },
  { value: 'paid', label: 'Paid' },
  { value: 'refunded', label: 'Refunded' },
];

const QUOTATION_STATUS_OPTIONS = [
  { value: 'draft', label: 'Draft' },
  { value: 'sent', label: 'Sent' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'converted', label: 'Converted' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'expired', label: 'Expired' },
];

const getOrderStatusColor = (status?: string) => {
  switch (status) {
    case 'placed': return '#3B82F6';
    case 'confirmed': return '#10B981';
    case 'processing': return '#F59E0B';
    case 'shipped': return '#8B5CF6';
    case 'delivered': return '#059669';
    case 'cancelled': return '#EF4444';
    default: return '#6B7280';
  }
};

const getPaymentStatusColor = (status?: string) => {
  switch (status) {
    case 'paid': return '#10B981';
    case 'partial': return '#F59E0B';
    case 'refunded': return '#8B5CF6';
    case 'unpaid': return '#EF4444';
    default: return '#6B7280';
  }
};

const getQuotationStatusColor = (status?: string) => {
  switch (status) {
    case 'sent': return '#3B82F6';
    case 'accepted': return '#10B981';
    case 'converted': return '#059669';
    case 'rejected': return '#EF4444';
    case 'expired': return '#6B7280';
    default: return '#F59E0B';
  }
};

const getAssignmentStatusColor = (status?: string) => {
  switch (status) {
    case 'active': return '#3B82F6';
    case 'inProgress': return '#F59E0B';
    case 'completed': return '#10B981';
    case 'cancelled': return '#EF4444';
    case 'transferred': return '#8B5CF6';
    default: return '#6B7280';
  }
};

const formatStatusLabel = (status?: string) => {
  if (!status) return 'None';
  return status
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatAssignmentDate = (date?: string) => {
  if (!date) return '';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const getChannelIcon = (channel: string) => {
  switch (channel) {
    case 'whatsapp':
      return 'logo-whatsapp';
    case 'phone':
      return 'call';
    case 'email':
      return 'mail';
    case 'facebook-messenger':
      return 'logo-facebook';
    case 'instagram':
      return 'logo-instagram';
    case 'telegram':
      return 'paper-plane';
    default:
      return 'chatbubble';
  }
};

const getChannelColor = (channel: string) => {
  switch (channel) {
    case 'whatsapp':
      return '#25D366';
    case 'phone':
      return '#0EA5E9';
    case 'email':
      return '#EA4335';
    case 'facebook-messenger':
      return '#1877F2';
    case 'instagram':
      return '#E4405F';
    case 'telegram':
      return '#0088CC';
    default:
      return colors.primary;
  }
};

// Accepts `t` since this needs translated names but stays a pure function of
// its arguments (module-level, not component-scoped) like its siblings above.
// Exported — the pane screens (ContactsListScreen/HomeScreen/InboxScreen)
// reuse it to label the specific identifier shown in the conversation pane's
// header, instead of duplicating this switch.
export const getChannelName = (channel: string, t: (key: any) => string) => {
  switch (channel) {
    case 'phone':
      return t('contactEdit.channel.phone');
    case 'whatsapp':
      return t('contactEdit.channel.whatsapp');
    case 'email':
      return t('contactEdit.channel.email');
    case 'facebook-messenger':
      return t('contactEdit.channel.facebook');
    case 'instagram':
      return t('contactEdit.channel.instagram');
    case 'telegram':
      return t('contactEdit.channel.telegram');
    default:
      return channel;
  }
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.background.surface,
    ...(Platform.OS === 'web' && {
      overflow: 'visible' as any,
      position: 'relative' as any,
    }),
  },
  compactContainer: {
    maxHeight: '90vh',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.surfaceRaised,
  },
  closeButton: {
    padding: spacing.xs,
  },
  content: {
    flex: 1,
    padding: spacing.lg,
    ...(Platform.OS === 'web' && {
      overflow: 'visible' as any,
      position: 'relative' as any,
    }),
  },
  compactContent: {
    paddingTop: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingBottom: spacing.lg,
  },
  avatarSection: {
    alignItems: 'center',
    paddingVertical: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    marginBottom: spacing.lg,
  },
  // Compact mode (master-detail pane flow) — tighter identity block overall,
  // so the avatar/name row and the channels row below it read as one group.
  phoneQuickActionsRow: {
    marginTop: spacing.sm,
    justifyContent: 'center',
  },
  avatarSectionCompactGroup: {
    paddingTop: 0,
    paddingBottom: spacing.md,
    marginBottom: spacing.md,
    backgroundColor: colors.background.surface,
    zIndex: 10,
    ...(Platform.OS === 'web' && {
      position: 'sticky' as any,
      top: 0,
    }),
  },
  avatarContainer: {
    position: 'relative',
    marginBottom: spacing.md,
  },
  avatar: {
    width: 100,
    height: 100,
    borderRadius: 50,
    backgroundColor: withAlpha(colors.primary, 0.13),
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarImage: {
    width: 100,
    height: 100,
    borderRadius: 50,
  },
  avatarText: {
    fontSize: typography.fontSize['2xl'],
    fontFamily: typography.fontFamily.bold,
    color: colors.primary,
  },
  avatarOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    borderRadius: 50,
    justifyContent: 'center',
    alignItems: 'center',
  },
  changePhotoButton: {
    flexDirection: 'row',
    alignItems: 'center',
    // gap (not marginLeft on the label) so the icon/label spacing stays
    // between them when rtl.rowDirection flips the row in Arabic.
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.secondary,
  },
  changePhotoText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  nameInputContainer: {
    width: '100%',
    marginTop: spacing.md,
    marginBottom: 0,
  },
  nameInput: {
    textAlign: 'center',
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
  },
  // Compact mode (the master-detail pane flow) — avatar inline with name
  // instead of this section's normal centered vertical stack.
  avatarSectionCompactRow: {
    alignItems: 'center',
    width: '100%',
  },
  // Save/Delete/quick-actions cluster on the far side of the avatar row,
  // replacing compact mode's separate header bar entirely.
  avatarActionsCluster: {
    alignItems: 'center',
    flexShrink: 0,
    gap: spacing.sm,
  },
  avatarContainerCompact: {
    position: 'relative',
    marginBottom: 0,
  },
  avatarCompact: {
    width: 52,
    height: 52,
    borderRadius: 26,
    backgroundColor: withAlpha(colors.primary, 0.13),
    justifyContent: 'center',
    alignItems: 'center',
  },
  avatarImageCompact: {
    width: 52,
    height: 52,
    borderRadius: 26,
  },
  avatarTextCompact: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.bold,
    color: colors.primary,
  },
  changePhotoButtonCompact: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 18,
    height: 18,
    borderRadius: 9,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderColor: colors.background.surface,
  },
  nameInputCompactWrap: {
    flex: 1,
    minWidth: 0,
  },
  nameInputContainerCompact: {
    width: '100%',
    marginTop: 0,
    marginBottom: 0,
  },
  nameInputCompact: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    // Reads as a plain header label, not a boxed form field — Input's own
    // base style otherwise always applies a border + surface background.
    borderWidth: 0,
    backgroundColor: 'transparent',
    paddingHorizontal: 0,
    paddingVertical: 0,
    minWidth: 0,
  },
  channelsRow: {
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.md,
  },
  // Tighter gap to the avatar/name row above, left-aligned to match the
  // compact row layout instead of the full-width centered stack.
  channelsRowCompact: {
    justifyContent: 'flex-start',
    marginTop: spacing.sm,
  },
  channelIconWrap: {
    width: 40,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
  },
  channelIconWrapCompact: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  channelIconMultiBadge: {
    position: 'absolute',
    bottom: -2,
    right: -2,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: colors.primary as string,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1.5,
    borderColor: colors.white,
  },
  channelsEditButton: {
    backgroundColor: colors.background.secondary,
  },
  formSection: {
    marginBottom: spacing.lg,
    ...(Platform.OS === 'web' && {
      overflow: 'visible' as any,
      position: 'relative' as any,
    }),
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.md,
    ...(Platform.OS === 'web' && {
      overflow: 'visible' as any,
      position: 'relative' as any,
    }),
  },
  sectionTitle: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
    marginBottom: spacing.md,
  },
  // Shared card chrome for the profile's info sections (Assignment, Orders,
  // Quotations, Documents) — formSection above has no visual container at
  // all, which read as disconnected text blocks once these sections stopped
  // being simple lists. Bordered + subtle shadow instead of a hard line.
  // Side-by-side layout for lighter compact sections (identifiers/notes,
  // documents/etc.) — heavier operational cards use profileCardsStack.
  profileCardsStack: {
    gap: spacing.sm,
  },
  profileCardsGridContent: {
    flexWrap: 'wrap',
    gap: spacing.sm,
    paddingBottom: spacing.xs,
  },
  profileCardInline: {
    flexGrow: 1,
    flexBasis: 280,
    minWidth: 260,
  },
  cardsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  cardsStacked: {
    flexDirection: 'column',
    flexWrap: 'nowrap',
    gap: spacing.md,
  },
  profileCardStacked: {
    flexBasis: 'auto',
    flexGrow: 0,
    width: '100%',
    padding: 0,
  },
  profileCardsStripContent: {
    gap: spacing.sm,
    paddingBottom: spacing.xs,
  },
  locationStacked: {
    alignItems: 'stretch',
  },
  locationThumbStacked: {
    flex: 0,
    height: 160,
    width: '100%',
  },
  profileCardInRow: {
    flexGrow: 1,
    flexBasis: 160,
    minWidth: 0,
    padding: spacing.sm,
  },
  profileCard: {
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.background.surface,
    padding: spacing.md,
    ...(Platform.OS === 'web'
      ? { boxShadow: '0 1px 2px rgba(0,0,0,0.04)' as any }
      : {
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.04,
          shadowRadius: 2,
          elevation: 1,
        }),
  },
  profileCardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
  },
  profileCardTitle: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  profileCardAction: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  profileCardEmpty: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    paddingVertical: spacing.xs,
  },
  profileCardRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
  },
  profileCardRowFirst: {
    borderTopWidth: 0,
  },
  profileCardItem: {
    paddingVertical: spacing.sm,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    gap: spacing.sm,
  },
  assignmentListScroll: {
    height: 260,
    paddingEnd: spacing.xs,
  },
  listMembershipSection: {
    marginTop: spacing.sm,
  },
  listMembershipScroll: {
    maxHeight: 150,
    paddingEnd: spacing.xs,
  },
  profileCardSectionLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    marginBottom: spacing.xs,
    textTransform: 'uppercase',
  },
  profileCardItemTop: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  profileCardIcon: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  profileCardRowTitle: {
    flexShrink: 1,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
  },
  profileCardNumberRow: {
    alignItems: 'center',
    gap: spacing.xs,
    minWidth: 0,
  },
  profileCardCopyButton: {
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: withAlpha(colors.primary, 0.06),
    flexShrink: 0,
  },
  profileCardRowSubtitle: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: 2,
  },
  profileCardRowTrailing: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
    flexShrink: 0,
  },
  profileCardActions: {
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: spacing.xs,
  },
  statusChip: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
    borderRadius: 999,
  },
  statusChipText: {
    fontSize: 11,
    fontFamily: typography.fontFamily.semibold,
  },
  profileCardActionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: 5,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: withAlpha(colors.primary, 0.07),
  },
  profileCardDangerAction: {
    backgroundColor: 'rgba(239, 68, 68, 0.14)',
  },
  profileCardActionButtonText: {
    fontSize: 11,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  profileCardIconButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.background.secondary,
  },
  profileCardDangerIconButton: {
    backgroundColor: 'rgba(239, 68, 68, 0.14)',
  },
  actionSheetBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.18)',
    justifyContent: 'flex-end',
    padding: spacing.md,
  },
  actionSheetCard: {
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.lg,
    borderWidth: 1,
    borderColor: colors.border,
    padding: spacing.sm,
    ...(Platform.OS === 'web'
      ? { maxWidth: 360, width: '100%', alignSelf: 'center', boxShadow: '0 12px 28px rgba(0,0,0,0.18)' as any }
      : {}),
  },
  actionSheetHeader: {
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.xs,
    paddingBottom: spacing.sm,
  },
  actionSheetTitle: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.semibold,
    color: colors.textDark,
  },
  actionSheetOption: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    borderRadius: spacing.borderRadius.md,
  },
  actionSheetDot: {
    width: 9,
    height: 9,
    borderRadius: 5,
  },
  actionSheetOptionText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  fieldGroup: {
    marginBottom: spacing.md,
  },
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.text.secondary,
    marginBottom: spacing.xs,
  },
  identifierRow: {
    flexDirection: 'column', // Stack vertically to fit in narrow pane
    marginBottom: spacing.sm,
    gap: spacing.xs,
  },
  identifiersScrollArea: {
    maxHeight: 260,
    paddingEnd: spacing.xs,
  },
  identifierTypeBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: colors.background.secondary,
    gap: spacing.xs,
    alignSelf: 'flex-start', // Align badge to start
  },
  identifierTypeText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
  identifierRowHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginBottom: spacing.xs,
  },
  identifierInput: {
    width: '100%',
  },
  removeIdentifierButton: {
    padding: spacing.xs,
  },
  identifierRowActions: {
    alignItems: 'center',
    gap: spacing.xs,
  },
  identifierPrimaryBadge: {
    backgroundColor: withAlpha(colors.primary, 0.13),
    borderRadius: spacing.borderRadius.sm,
    paddingHorizontal: spacing.xs,
    paddingVertical: 1,
  },
  identifierPrimaryBadgeText: {
    fontSize: 10,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
  },
  setPrimaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 3,
    paddingHorizontal: spacing.xs,
    paddingVertical: spacing.xs / 2,
  },
  setPrimaryButtonText: {
    fontSize: 11,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  quickAddSiblingButton: {
    flexDirection: 'row',
    alignItems: 'center',
    alignSelf: 'flex-start',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs / 2,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: withAlpha(colors.primary, 0.08),
  },
  quickAddSiblingButtonText: {
    fontSize: 12,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  locationButton: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.secondary,
    gap: spacing.sm,
  },
  locationButtonText: {
    flex: 1,
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
  },
  locationDetailRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  locationDetailInput: {
    flex: 1,
  },
  // Stacked field spacing, reused by compact mode's right-hand fields column.
  locationNationalityInput: {
    marginTop: spacing.sm,
  },
  // Compact (pane) mode — map thumb on one side, fields stacked on the other.
  locationCompactRow: {
    gap: spacing.md,
    alignItems: 'flex-start',
  },
  locationThumb: {
    flex: 1,
    minHeight: 160,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
    overflow: 'hidden',
  },
  locationThumbImage: {
    width: '100%',
    height: '100%',
  },
  locationThumbPin: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: [{ translateX: -14 }, { translateY: -28 }],
    width: 28,
    height: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  locationThumbPlaceholder: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
  },
  locationThumbText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
    textAlign: 'center',
  },
  locationFieldsColumn: {
    flex: 1,
    minWidth: 0,
  },
  mapsUrlToggle: {
    alignSelf: 'flex-start',
    marginTop: spacing.sm,
  },
  mapsUrlToggleText: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  mapsUrlRow: {
    alignItems: 'center',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
  mapsUrlInput: {
    flex: 1,
  },
  mapsUrlButton: {
    width: 40,
    height: 40,
    borderRadius: spacing.borderRadius.md,
    backgroundColor: colors.primary,
    alignItems: 'center',
    justifyContent: 'center',
  },
  mapsUrlButtonDisabled: {
    opacity: 0.5,
  },
  // Height-capped — with many tags the list scrolls internally instead of
  // pushing the rest of the form down or overflowing the pane's width.
  tagsScrollArea: {
    maxHeight: 140,
    marginBottom: spacing.sm,
  },
  tagsContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    alignContent: 'flex-start',
  },
  tag: {
    flexDirection: 'row',
    alignItems: 'center',
    maxWidth: '100%', // long tag text ellipsizes instead of forcing the chip past the pane's edge
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.sm,
    backgroundColor: withAlpha(colors.primary, 0.13),
    gap: spacing.xs,
  },
  tagText: {
    flexShrink: 1,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary,
  },
  tagSearchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: spacing.borderRadius.sm,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.secondary,
    marginBottom: spacing.sm,
  },
  tagSearchInput: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
    paddingVertical: Platform.OS === 'web' ? spacing.xs : 0,
  },
  addTagContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  addTagButton: {
    padding: spacing.sm,
  },
  addButtonContainer: {
    position: 'relative',
    zIndex: 10000,
    elevation: 30,
    ...(Platform.OS === 'web' && {
      zIndex: 10000,
      isolation: 'isolate' as any,
    }),
  },
  addButtonContainerOpen: {
    ...(Platform.OS === 'web' && {
      zIndex: 2147483647,
      isolation: 'isolate' as any,
    }),
  },
  addButton: {
    padding: spacing.xs,
  },
  identifierMenuBackdrop: {
    flex: 1,
  },
  // Overrides the old in-card absolute placement: the menu now lives in a
  // Modal at window coordinates (see openIdentifierMenu).
  identifierMenuFloating: {
    position: 'absolute',
    right: undefined,
    marginTop: 0,
  },
  identifierMenu: {
    position: 'absolute',
    top: 40,
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: colors.literal.black,
    shadowOffset: { width: 0, height: 12 },
    shadowOpacity: 0.25,
    shadowRadius: 24,
    elevation: 9999,
    zIndex: 999999,
    overflow: 'hidden',
    minWidth: 200,
    ...(Platform.OS === 'web' && {
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      zIndex: 2147483647,
      position: 'absolute' as any,
      isolation: 'isolate' as any,
      pointerEvents: 'auto' as any,
      // Ensure it's above everything - use transform to create new stacking context
      willChange: 'transform' as any,
      transform: [{ translateZ: 0 }] as any,
      // Additional web-specific properties to ensure visibility
      backfaceVisibility: 'hidden' as any,
      WebkitBackfaceVisibility: 'hidden' as any,
    }),
  },
  menuItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    paddingHorizontal: spacing.lg,
    gap: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  menuItemText: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.regular,
    color: colors.textDark,
  },
  attachmentItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: spacing.md,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    backgroundColor: colors.background.secondary,
    marginBottom: spacing.sm,
    gap: spacing.sm,
  },
  attachmentInfo: {
    flex: 1,
  },
  attachmentName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    color: colors.textDark,
  },
  attachmentSize: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    marginTop: spacing.xs / 2,
  },
  emptyText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.secondary,
    fontStyle: 'italic',
  },
  topActionBar: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
  },
  topActionSaveButton: {
    flex: 1,
  },
  topActionSaveIconButton: {
    minWidth: 72,
    height: 36,
    borderRadius: 18,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.xs,
    paddingHorizontal: spacing.sm,
    backgroundColor: colors.primary as string,
  },
  topActionSaveIconButtonText: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.white,
  },
  topActionDeleteButton: {
    width: 44,
    height: 44,
    borderRadius: spacing.borderRadius.md,
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#EF4444',
    backgroundColor: 'rgba(239, 68, 68, 0.14)',
  },
  topActionDeleteButtonCompact: {
    width: 36,
    height: 36,
    borderRadius: 18,
  },
  iconPickerBackdrop: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  iconPickerContainer: {
    position: 'absolute',
    maxWidth: 220,
  },
  iconPickerCard: {
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: spacing.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 8,
    padding: spacing.sm,
    minWidth: 160,
  },
  iconPickerTitle: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.semibold,
    color: colors.text.secondary,
    textTransform: 'uppercase',
    marginBottom: spacing.xs,
  },
  iconPickerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.xs / 2,
  },
  iconPickerRowText: {
    flex: 1,
    fontSize: 13,
    fontFamily: typography.fontFamily.regular,
    color: colors.text.primary,
  },
  iconPickerPrimaryTag: {
    fontSize: 10,
    fontFamily: typography.fontFamily.medium,
    color: colors.primary as string,
  },
});
